// SVG node-graph editor. Renders the device as boxes + wires, drags nodes, draws
// wires only along legal routes, and highlights legal targets while connecting.
// Visual attributes are inline so the PNG export matches.

import type { ConnectionKind, DeviceModel, DeviceNode, NodeKind, PortDirection } from "../models/types";
import { fullLabel, hangsUnderHeader, isSingleInput, parseRef, ref } from "../models/types";
import type { Plan, PlanConnection } from "../core/plan";
import { hasConnection, LEVEL_MIN_DB } from "../core/plan";
import {
  canConnect,
  channelTapAheadOfMute,
  completesLinkedPatch,
  directOutTarget,
  dropWire,
  isFixedConnection,
  isLastRequiredSource,
  isNodeInactive,
  legalSources,
  legalTargets,
  mixSendLocks,
  monoPairOf,
  pairPrimary,
  partnerChannel,
  possibleSources,
  possibleTargets,
  ruleKind,
  sendIsPreFader,
  tapAheadOfDucker,
  upstreamNodes,
} from "../core/routing";
import { baseName, exportSvgToPdf, exportSvgToPng } from "../core/storage";
import { getSettings } from "../core/settings";
import type { ExportOptions, SaveResult } from "../core/storage";
import { cueAddrOf, oscAssign } from "../core/control/translate";
import { planColorHex, SD_REC_TRACK_COUNT_DEFAULT } from "../core/control/params";
import { nodeRateDisabled, trackCountCeiling } from "../core/constraints";
import { NOTE_BOT_GAP, NOTE_LINE_H, NOTE_PAD_Y, NOTE_TOP_GAP, clipNote, fitScale, notePanelHeight } from "./graph-text";
import { sendlessNote } from "./send-fields";
import { isChord } from "./keys";
import { mouseMovedUnpressed, preserveFocus } from "./dom";
import { setTip, spoken } from "./glyph";
import { t } from "../i18n";

const SVGNS = "http://www.w3.org/2000/svg";

const NODE_W = 184;
const NODE_H = 44;
const COL_GAP = 256;
// Row pitch. The gutter it leaves (ROW_GAP - NODE_H) is not slack: a Rec Point tap
// rises into it and runs across it, so a pitch that leaves less than roughly
// TAP_RISE plus clearance makes those wires hug the underside of the node above
// and read as belonging to it.
const ROW_GAP = 68;
const MARGIN = 40;

// Vertical gap between a parent channel and the ducker hung directly under it.
const DUCKER_GAP = 8;

// Jack geometry: the socket ring, its pin at rest and lit, and the radius a jack
// grows to while it is a candidate during a connect drag.
const JACK_R = 6;
const PIN_R_OFF = 2.4;
const PIN_R_ON = 3;
const JACK_R_CANDIDATE = 8;

// The clickable target is widened past the visible jack so the small connector is
// easy to grab. Stays within the column/row gaps so the halos of neighboring ports
// never overlap.
const PORT_HIT_R = 14;

// Visible wire is 2-3.5px; the clickable band along it is widened to this so
// the thin curve is easy to select.
const WIRE_HIT_W = 14;

// The Rec Point tap jack on a channel's top edge: the direct-out / recording
// pickup, which the block diagram places ahead of the fader and Ducker. It sits
// left of center on purpose — the STEREO-link tie lands on the top-center, and a
// tap taken before the strip's mixer stage belongs at its upstream end. TAP_RISE
// is the straight riser its wire climbs before sweeping across, so the route
// visibly leaves the faceplate upward instead of joining the bus sends at the
// right edge. Kept under the row gutter (ROW_GAP - NODE_H) so the riser stands in
// clear space rather than behind the node above.
const TAP_CX = 62;
const TAP_RISE = 14;
// The frame around a node on the CUE bus's path: how far outside the faceplate it runs, which
// clears the jacks on its edges and leaves a gap between the frames of two nodes a row apart,
// and its stroke.
const CUE_FRAME_PAD = 7;
const CUE_FRAME_W = 2;
// The tap's pointer target: a band sized to the jack itself, not to the row
// gutter. Reaching further up would be free space only in the default layout —
// nodes drag freely, so a tall band lands on whatever the user parked above and
// steals that node's drag — and reaching further down would eat into the header,
// which is where a node is grabbed. Kept close to the jack, both stay reliable.
const TAP_HIT_W = 26;
const TAP_HIT_UP = 12;
const TAP_HIT_DOWN = 8;

// Pointer travel (screen px) past which a port press becomes a connect drag
// rather than a click. A click on an input port selects its incoming wire.
const DRAG_THRESHOLD = 4;

// A node press held this long (ms) without moving past LONG_PRESS_TOLERANCE (px)
// traces the node's signal path instead of selecting / dragging it. The tolerance
// lets the press survive a little jitter before it commits to a drag.
const LONG_PRESS_MS = 450;
const LONG_PRESS_TOLERANCE = 6;

// Zoom bounds shared by the wheel, pinch, and fit-to-view paths.
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 2.5;

// A wheel gesture ends when no wheel event has arrived for this long (ms); the next event
// starts a new one, which takes its own axis.
const WHEEL_GESTURE_GAP_MS = 150;

const LABEL_FONT = '"SF Mono", "SFMono-Regular", "Menlo", "Cascadia Code", "Consolas", monospace';

// Label geometry. The label starts at LABEL_X and must clear the header button,
// whose visible box begins at NODE_W-25-8; a wider single line is scaled down to
// fit. A node with a sublabel stacks two tiers within the fixed header height.
const LABEL_X = 24;
const LABEL_MAX_W = NODE_W - 25 - 8 - 5 - LABEL_X; // 122

const LABEL_FS = 12;
const LABEL_SUB_FS = 9;
const LABEL_TIER1_Y = 16;
const LABEL_TIER2_Y = 32;

// Note panel: an annotation area that lives INSIDE the node frame, below the
// header, separated by a seam. The node grows to contain it; it can be collapsed
// to the header alone and re-expanded with the header button.
const NOTE_INSET = 9; // well inset from the node's left/right edges
const NOTE_PAD_X = 10; // text inset inside the well
const NOTE_FONT_SIZE = 11;

export type ThemeName = "dark" | "light";
/** Which name the canvas shows on each node: the planner's fixed label
 *  ("CH 1") or the device CH SETTING name ("ch 1"). */
export type LabelSource = "model" | "device";

/** The three colour families a wire can wear. Six connection kinds share them,
 *  because colour is the wrong carrier for the distinctions the other three make:
 *  `key` is a `source` that happens to feed a ducker, `sendSwitch` is a `send`
 *  with an on/off, and `record` is an output selection that lands on a track. All
 *  three are already told apart by their endpoints, and spending a hue on each
 *  cost more than it bought — measured, `record` and `source` were 1.6 OKLab
 *  apart under deuteranopia, which is to say indistinguishable. */
export type WireGroup = "select" | "send" | "out";

/** Which family each connection kind is drawn in. Exhaustive by type, so adding a
 *  kind will not compile until it is placed — the previous shape was an index
 *  signature with a grey fallback, where a missed kind drew silently. */
export const WIRE_GROUP: Record<ConnectionKind, WireGroup> = {
  source: "select",
  key: "select",
  send: "send",
  sendSwitch: "send",
  patch: "out",
  record: "out",
};

interface Palette {
  /** Page background behind the graph — must stay in sync with the matching
   *  --canvas-bg value in style.css (the theme-parity rule). Used as the raster
   *  background when exporting under a fixed theme, where the live CSS variable
   *  may belong to the other theme. */
  canvasBg: string;
  /** Single brushed-metal node fill; the kind is shown by the rail color. */
  nodeFill: string;
  nodeStroke: string;
  nodeHi: string;
  rail: Record<NodeKind, string>;
  screwFill: string;
  screwStroke: string;
  label: string;
  /** Jack-socket hole and (unconnected) pin. */
  portOuter: string;
  portPinOff: string;
  wire: Record<WireGroup, string>;
  tempWire: string;
  /** Legal-target highlight shown on input ports while connecting. */
  legalFill: string;
  legalStroke: string;
  /** "Could route, but occupied" highlight: outline only, no fill, so a full
   *  target reads as a possibility without looking connectable. */
  possibleStroke: string;
  /** Outline for nodes unavailable at the current sample rate. */
  warn: string;
  /** Ink of the OFF / "?" badge text printed on `warn`. */
  warnInk: string;
  /** Accent for the PRE (pre-fader) send marker; the brand LED amber. */
  pre: string;
  /** Ink of the "PRE" label beside that marker, on the canvas. */
  preInk: string;
  /** Fill opacity of a node's secondary legend (the sublabel) over `nodeFill`. */
  sublabelOpacity: number;
  /** In-frame note panel: recessed-well tint and ink. */
  noteWell: string;
  noteInk: string;
  /** What is cued: the frames, the lines into a monitor and the CUE badge's face. */
  cue: string;
  /** Ink of the CUE badge printed on `cue`. */
  cueInk: string;
}

// Exported for src/ui/palette.contract.test.ts, which is the only thing holding
// this and style.css's --w-* to the same values.
export const PALETTES: Record<ThemeName, Palette> = {
  dark: {
    canvasBg: "#14110d",
    nodeFill: "#221b12",
    nodeStroke: "#4a3d27",
    nodeHi: "rgba(255,255,255,0.05)",
    rail: { input: "#3f7fb0", channel: "#9a6fc0", bus: "#5fb088", output: "#c79a4e", ducker: "#cf6f7e" },
    screwFill: "#0e0b07",
    screwStroke: "#5a4b30",
    label: "#f1e8d6",
    portOuter: "#0c0a07",
    portPinOff: "#241d12",
    wire: {
      select: "#59b2ff",
      send: "#76d690",
      out: "#ffb347",
    },
    tempWire: "#caa86a",
    legalFill: "#1d3b2a",
    legalStroke: "#7fd0a0",
    possibleStroke: "#5a7d6a",
    warn: "#e2794e",
    warnInk: "#14110d",
    pre: "#ffc24d",
    preInk: "#ffc24d",
    sublabelOpacity: 0.6,
    noteWell: "rgba(0,0,0,0.24)",
    noteInk: "#e9ddc3",
    cue: "#84e3ff",
    cueInk: "#0e0c08",
  },
  light: {
    canvasBg: "#e8e3d8",
    nodeFill: "#f4efe5",
    nodeStroke: "#b7a884",
    nodeHi: "rgba(255,255,255,0.92)",
    rail: { input: "#2f6f9e", channel: "#7a4fa0", bus: "#2f8f63", output: "#b07a1e", ducker: "#b04a5e" },
    screwFill: "#a99c80",
    screwStroke: "#857a5e",
    label: "#2a2418",
    portOuter: "#d9d0bd",
    portPinOff: "#cabd9f",
    wire: {
      select: "#1f6fc8",
      // The teal the light theme used for `sendSwitch`, not the green it used for
      // `send`: on the light board it holds 3.97 against the canvas where the green
      // holds less, and the two were 5.3 OKLab apart — too close to be two colours.
      send: "#13836f",
      out: "#b8700a",
    },
    tempWire: "#8d8064",
    legalFill: "#cde7d6",
    legalStroke: "#2f8f63",
    possibleStroke: "#587d69",
    warn: "#a8461a",
    warnInk: "#ffffff",
    pre: "#e8920f",
    preInk: "#755407",
    sublabelOpacity: 0.75,
    noteWell: "rgba(95,78,42,0.10)",
    noteInk: "#3c3320",
    cue: "#0b7285",
    cueInk: "#ffffff",
  },
};

export type Selection = { type: "node"; id: string } | { type: "conn"; from: string; to: string } | null;

/** A jack a wire can be drawn from — a port, or a channel's Rec Point tap (`tap`, which
 *  carries the channel's own `out` ref) — with the ports a drag from it can end on. */
export interface ConnectOrigin {
  ref: string;
  dir: PortDirection;
  tap: boolean;
  targets: string[];
}

export interface GraphCallbacks {
  onSelect: (sel: Selection) => void;
  onStatus: (message: string) => void;
  onChange: () => void;
  // Fired whenever the shelved-node set changes, so the host can persist it.
  onHiddenChange: (hidden: string[]) => void;
  // Asked before a gesture writes the plan. False refuses it: nothing is written, and the
  // host has said why.
  mayEdit: () => boolean;
}

interface Pt {
  x: number;
  y: number;
}

/** A node's slot in the default layout — where the board draws it while the plan
 *  holds no position of its own. */
function defaultLayoutPos(node: DeviceNode): Pt {
  return { x: MARGIN + node.pos.col * COL_GAP, y: MARGIN + node.pos.row * ROW_GAP };
}

// Two positions count as the same slot when they are within this. A pair the operator
// dragged carries float error: the partner's own coordinate is `primary + offset` computed
// once and re-added at a different base on every move, so a pair that never left its slot
// can sit ~1e-13 px off the value an alignment would write. Sub-pixel, since a write nobody
// can see is still a plan write, and on the device-follow path it lands outside every edit
// funnel.
const ALIGN_SLOP = 0.01;

export class Graph {
  private svg!: SVGSVGElement;
  private viewport!: SVGGElement;
  private wireLayer!: SVGGElement;
  private nodeLayer!: SVGGElement;
  private overlay!: SVGGElement;
  // What is cued, drawn over everything else and kept out of an image export.
  private cueLayer!: SVGGElement;
  private shelf!: HTMLDivElement;
  // Floating contextual bar shown while several nodes are multi-selected.
  private selbar!: HTMLDivElement;
  private host!: HTMLElement;

  private model: DeviceModel;
  private plan: Plan;
  private readonly cb: GraphCallbacks;

  private nodeById = new Map<string, DeviceNode>();
  private nodeEls = new Map<string, SVGGElement>();
  private portEls = new Map<string, SVGCircleElement>();
  private portPinEls = new Map<string, SVGCircleElement>();
  // Rec Point tap jacks, keyed by channel id. Separate from portEls because the
  // tap shares the channel's `ch:out` ref (the plan is unchanged by it) — one ref
  // would otherwise collide in a single map.
  private tapEls = new Map<string, SVGCircleElement>();
  private tapPinEls = new Map<string, SVGCircleElement>();
  // STEREO-link tie elements, keyed by the pair's primary (odd) channel id, so a
  // drag can redraw the tie in place as the pair moves.
  private stereoLinkEls = new Map<string, SVGGElement>();

  private palette: Palette = PALETTES.dark;
  private themeName: ThemeName = "dark";
  // Which name the canvas shows: the planner's fixed label ("CH 1") or the
  // device CH SETTING name held in plan.nodeNames ("ch 1"). Default is the
  // planner label, so the device names a fetch/seed brings in are opt-in.
  private labelSource: LabelSource = "model";
  // Hide the off / -∞ sends (params.on === false or level at -∞) from the canvas.
  // Off by default so every fixed send shows (dimmed); the toolbar toggle declutters
  // a board where most of the always-wired CH → MIX/FX sends sit at -∞.
  private hideOffSends = false;
  private disabledNodes = new Set<string>();
  // The microSD Rec slots the node layer was last built without because Track Count or the
  // rate's ceiling gates them. A rate change moves that set as well as the disabled one.
  private drawnGated = new Set<string>();
  // Nodes still showing their plan value after a device readback (a read of their
  // settings or selector failed). Mirrors plan.unreadNodes; empty when the plan has
  // no device provenance (new / loaded / hand-edited plan).
  private unreadNodes = new Set<string>();
  // Node ids collapsed off the canvas into the bottom shelf. Kept in sync with
  // plan.hidden; a shelved node is hidden along with its wires.
  private hidden = new Set<string>();
  // Node ids whose in-frame note panel is minimized to the header. Synced with
  // plan.noteCollapsed.
  private collapsed = new Set<string>();

  private pan: Pt = { x: 0, y: 0 };
  private zoom = 1;
  // Auto-fit the diagram to the viewport until the user pans or zooms by hand.
  // A ResizeObserver re-fits on the first real layout (the webview can report a
  // stale size during construction) and on window resize while this stays true.
  private autoFit = true;
  private selection: Selection = null;
  // The node the board puts in the tab order (the only one with tabindex 0): the one keyboard
  // focus last rested on or that was last selected, else the first drawn.
  private focusNode: string | null = null;
  // The plan the node layer was last built for. Keyboard focus is carried across a rebuild of
  // the same plan only: the node it stood on belongs to a replaced plan, and a key still held
  // there must reach nothing in the one that took its place.
  private builtFor: Plan | null = null;
  // The plan the shelf and the selection bar were each last built for. Focus on a chip or a
  // bar button is carried across a rebuild of the same plan only, as on the board.
  private chromeBuiltFor = new WeakMap<HTMLElement, Plan>();
  // Ctrl/Cmd-click builds a multi-selection of nodes to shelve together. The
  // anchor (shown in the inspector) is selection.id; this set holds it plus any
  // others. Empty whenever selection is a connection or null.
  private selectedNodes = new Set<string>();
  // Path-trace highlight: the upstream signal closure of a double-clicked node
  // (the node plus every input/channel/bus that feeds it through live wiring).
  // Empty when no trace is active. Independent of the selection; cleared by any
  // selection change so it never lingers behind a fresh focus.
  private pathNodes = new Set<string>();
  // The node the trace was taken for. The closure is taken again from it before every
  // wire and node repaint, so an edit, an undo or a device read under the trace moves it.
  private pathRoot: string | null = null;
  // The cued strips (setCues). While any is, the board fades what the CUE bus does not carry.
  private cued: readonly string[] = [];
  // The nodes the CUE bus's path runs through and the monitors that play it, taken again
  // before every repaint (recue). Empty while nothing drawn is cued.
  private cuePathNodes = new Set<string>();
  private cueMonitors: string[] = [];
  // True while an image export is being taken, which carries nothing of the cue.
  private cueHeld = false;
  // A trace was taken and nothing feeds its node. The selection then lights nothing and
  // fades nothing, as the status line says; any selection change ends it.
  private pathEmpty = false;

  // transient interaction state. `link` is a STEREO-linked, visible partner that
  // moves with the dragged node (keeping its offset), plus the pair to redraw the
  // tie for. Null when the dragged node has no visible linked partner.
  private dragNode: {
    id: string;
    grabDx: number;
    grabDy: number;
    link: { partner: string; dx: number; dy: number; pair: [string, string] } | null;
    // True once the pointer actually moved the node, so a plain select-click (no
    // movement) does not mark the plan dirty.
    moved: boolean;
  } | null = null;
  // A port press: starts as "pending", becomes a "connecting" rubber-band once
  // dragged past the threshold, or "noop" if the dragged port has no legal
  // partner. On release a non-connecting input press selects its incoming wire.
  private connect: {
    ref: string;
    dir: PortDirection;
    // True when the press landed on a channel's Rec Point tap rather than its
    // right-edge output. The two are separate origins: each offers only the
    // routes that leave from it.
    tap: boolean;
    startX: number;
    startY: number;
    mode: "pending" | "connecting" | "noop";
  } | null = null;
  private tempWire: SVGPathElement | null = null;
  private panning: { startX: number; startY: number; panX: number; panY: number } | null = null;
  // Active touch points, used to detect a two-finger pinch. While two are down a
  // pinch zooms/pans the canvas and single-pointer gestures are suspended.
  private pointers = new Map<number, { x: number; y: number }>();
  // left/top cache the svg's viewport origin for the gesture: it never moves
  // (the pinch transforms an inner <g>), so reading it once avoids a forced
  // reflow on every move frame.
  private pinch: { lastDist: number; lastCx: number; lastCy: number; left: number; top: number } | null = null;
  // The wheel gesture under way: the axis its first event moved along, and when its latest
  // event arrived. A horizontal gesture pans the board, a vertical one zooms it.
  private wheelGesture: { axis: "x" | "y"; last: number } | null = null;
  // Floating HTML textarea for editing a node's note in place on the canvas.
  private noteEditor: { id: string; el: HTMLTextAreaElement } | null = null;
  // In-flight long-press on a node: a timer that traces the node's signal path if
  // the pointer holds still for LONG_PRESS_MS. Cleared the moment the pointer moves
  // past the tolerance (the press became a drag) or lifts (it was a plain click).
  private longPress: { id: string; timer: ReturnType<typeof setTimeout>; x: number; y: number } | null = null;
  // Last node pointerdown, used to detect a double-press that opens the note editor.
  // A real dblclick event is unreliable here: onPointerDown calls preventDefault(),
  // which suppresses the browser's compatibility mouse events.
  private lastNodeClick: { id: string; time: number } | null = null;

  constructor(host: HTMLElement, model: DeviceModel, plan: Plan, cb: GraphCallbacks) {
    this.model = model;
    this.plan = plan;
    this.cb = cb;
    this.adoptPlanState();
    this.buildScaffold(host);
    this.render();
    this.fitView();
  }

  setModel(model: DeviceModel, plan: Plan): void {
    // A gesture in progress belongs to the plan it began on. A different plan here is that one
    // replaced, so the gesture ends and reports nothing: a pointer still held writes nothing
    // into the plan that took its place.
    if (plan !== this.plan) {
      this.dragNode = null;
      this.lastNodeClick = null;
      this.endAllPointers();
    }
    // The note being edited goes with the selection: a Fetch, the Live-sync read and a .urxf
    // import re-author the plan's values here, and a file or a model switch replaces it.
    this.dropNoteEditor();
    this.model = model;
    this.plan = plan;
    this.selection = null;
    this.selectedNodes.clear();
    // A trace belongs to the selection it was taken from, which goes here too.
    this.clearTrace();
    this.adoptPlanState();
    // Before the draw and the fit: a document arriving with a pair already STEREO-linked
    // had no edit funnel to snap its partner, so this is where the two loading paths
    // (loadPlan and rerenderPlan, which is Fetch / the Live-sync read / a .urxf import)
    // get what linking in the app gives. Positions decide the fit, so a snap taken
    // afterwards would frame the board it replaced. `nodeById` is what the align reads for
    // a node's slot, and `render` rebuilds it, so it is seeded here rather than left to the
    // model this view is replacing.
    this.nodeById.clear();
    for (const node of this.model.nodes) this.nodeById.set(node.id, node);
    this.alignLinkedPairs();
    this.render();
    this.fitView();
  }

  /** Re-render the current (same-reference) plan after it was mutated in place — a
   *  device-follow readback, or an undo. Unlike setModel this keeps the viewport, so
   *  reflecting a change does not disturb the user. It re-derives the plan-backed view
   *  state and re-validates the selection unconditionally, because the caller cannot
   *  tell which of those a given mutation moved — and the sets are write-back caches
   *  (commitHidden pushes this.hidden into the plan), so a stale one would resurrect
   *  the state that was just undone. */
  refresh(): void {
    this.adoptPlanState();
    this.dropSelectionIfHidden();
    this.render();
  }

  // The view state mirrored out of the plan: the shelved set, the note-collapse set,
  // and the device provenance (plan.unreadNodes holds exactly the nodes a read failed
  // on; no provenance — a plan never fetched — means nothing is flagged). Every
  // place that adopts a plan goes through here so none of the three is forgotten.
  private adoptPlanState(): void {
    this.hidden = new Set(this.plan.hidden);
    this.collapsed = new Set(this.plan.noteCollapsed);
    this.unreadNodes = new Set(this.plan.unreadNodes ?? []);
  }

  setTheme(name: ThemeName): void {
    this.palette = PALETTES[name];
    this.themeName = name;
    this.render();
  }

  /** Choose whether the canvas shows planner labels or device names. */
  setLabelSource(source: LabelSource): void {
    this.labelSource = source;
    this.render();
  }

  /** Whether the canvas hides off / -∞ sends (toolbar declutter toggle). */
  isHideOffSends(): boolean {
    return this.hideOffSends;
  }

  /** Show or hide the off / -∞ sends; repaints the wires. */
  setHideOffSends(hide: boolean): void {
    this.hideOffSends = hide;
    this.redrawWires();
  }

  /** Whether the board is already drawn against this disabled set and against the plan's
   *  gated record slots. The dim and the dashed outline are all the set contributes, and the
   *  slots Track Count and the rate's ceiling leave out are all the rate contributes besides,
   *  so with both equal the board drawn is the same one. */
  hasDisabledNodes(ids: string[]): boolean {
    return sameSet(new Set(ids), this.disabledNodes) && sameSet(this.gatedSlots(), this.drawnGated);
  }

  /** Mark nodes unavailable at the current sample rate (dimmed + dashed outline). The
   *  set is stored either way, and a selection the rate has taken off the board is dropped
   *  whether or not the host is on screen. The board is redrawn only when the set or the
   *  gated record slots moved AND the host is on screen: an unchanged pair draws the same
   *  board, and a hidden one is redrawn from the plan when the view comes back (the caller
   *  carries that with graphDirty). */
  setDisabledNodes(ids: string[]): void {
    const same = this.hasDisabledNodes(ids);
    this.disabledNodes = new Set(ids);
    const selected = this.selection;
    this.dropSelectionIfHidden();
    if ((same && this.selection === selected) || this.host.hidden) return;
    this.render();
  }

  /** The microSD Rec slots Track Count and the rate's ceiling leave off the board now. */
  private gatedSlots(): Set<string> {
    return new Set(this.model.nodes.filter((n) => this.sdRecSlotInactive(n.id)).map((n) => n.id));
  }

  /** Repaint nodes after a node-parameter change (e.g. a channel muted). */
  repaintNodes(): void {
    this.renderNodes();
    // renderNodes rebuilds every node with its jacks reset to off; restore the lit
    // ports and the selection frame, as render() and the note repaints do.
    this.refreshPortStates();
    this.highlightSelectedNode();
  }

  /** Repaint only the given nodes in place (a device-follow direct change touched
   *  just these), then restore port glow, wires, and the selection frame. Rebuilds
   *  a handful of node elements instead of every one, as render() would. */
  repaintDirtyNodes(ids: string[]): void {
    for (const id of ids) this.repaintNode(id);
    // redrawWires ends with refreshPortStates, so the port glow is restored there.
    this.redrawWires();
    this.highlightSelectedNode();
    // The rebuilt nodes' jacks come back at rest, so a connect drag under way lights them again.
    this.repaintCandidates();
  }

  /** Rebuild one node's <g> in place. makeNode re-registers this node's nodeEls /
   *  portEls / portPinEls / tap-jack entries (same keys), so the old port elements
   *  just detach with the replaced group. Caller batches the port / wire /
   *  selection refresh. */
  private repaintNode(id: string): void {
    if (this.isHidden(id)) return;
    const node = this.nodeById.get(id);
    if (!node) return;
    const focused = this.focusedNodeId() === id;
    const old = this.nodeEls.get(id);
    const g = this.makeNode(node);
    if (old) old.replaceWith(g);
    else this.nodeLayer.append(g);
    this.syncRovingNode();
    if (focused) this.refocusNode(id);
  }

  /** Set or clear a node's free-text note and repaint its in-frame panel. */
  setNote(id: string, text: string): void {
    this.plan.notes ??= {};
    if (text.trim()) {
      this.plan.notes[id] = text;
    } else {
      delete this.plan.notes[id];
      // A note-less node has nothing to collapse; drop any stale collapse flag.
      if (this.collapsed.delete(id)) this.plan.noteCollapsed = [...this.collapsed];
    }
    // The plan write and its funnel are paired here rather than at the call site,
    // so a second caller cannot mutate the plan without reporting the change.
    this.cb.onChange();
    this.renderNodes();
    // A note panel resizing shifts any hung children (a ducker, the SD Rec slots),
    // so redraw the wires (which ends with refreshPortStates) to reattach them.
    this.redrawWires();
    this.highlightSelectedNode();
  }

  /** Minimize a node's note to its header, or re-expand it. */
  toggleNoteCollapse(id: string): void {
    if (!this.noteLines(id).length) return;
    if (!this.cb.mayEdit()) return;
    const nowCollapsed = !this.collapsed.has(id);
    if (nowCollapsed) this.collapsed.add(id);
    else this.collapsed.delete(id);
    this.plan.noteCollapsed = [...this.collapsed];
    if (this.noteEditor?.id === id) {
      this.closeNoteEditor();
    } else {
      this.renderNodes();
      this.redrawWires();
      this.highlightSelectedNode();
    }
    this.cb.onChange();
    this.cb.onStatus(nowCollapsed ? t().status.noteMinimized : t().status.noteExpanded);
  }

  // --- inline note editing -------------------------------------------------

  /** Open a floating textarea over the node's note panel, editing in context. */
  private openNoteEditor(id: string): void {
    if (!this.cb.mayEdit()) return;
    this.closeNoteEditor();
    this.select({ type: "node", id });
    // Editing always shows the panel, so un-collapse first. That drops the flag
    // from the plan whether or not the edit that follows changes the text, so it
    // reports the change itself — otherwise opening and closing a collapsed note
    // leaves the plan un-collapsed with nothing marked as edited.
    if (this.collapsed.delete(id)) {
      this.plan.noteCollapsed = [...this.collapsed];
      this.cb.onChange();
    }
    const ta = document.createElement("textarea");
    ta.className = "note-edit-overlay";
    ta.value = this.plan.notes?.[id] ?? "";
    ta.placeholder = t().inspector.notesPlaceholder;
    ta.spellcheck = false;
    this.host.append(ta);
    this.noteEditor = { id, el: ta };
    // Re-render so the panel's text is hidden behind the editor.
    this.renderNodes();
    this.redrawWires();
    this.highlightSelectedNode();
    this.positionNoteEditor();
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);

    ta.addEventListener("input", () => this.setNote(id, ta.value));
    ta.addEventListener("keydown", (e) => {
      e.stopPropagation();
      // A key pressed during an IME composition belongs to the composition: Escape cancels
      // the conversion and Enter commits it, and neither closes the editor.
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === "Escape" || (e.key === "Enter" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        this.closeNoteEditor();
      }
    });
    ta.addEventListener("blur", () => this.closeNoteEditor());
    ta.addEventListener("pointerdown", (e) => e.stopPropagation());
  }

  /** Keep the open editor pinned over its node's panel through pan / zoom. */
  private positionNoteEditor(): void {
    const ed = this.noteEditor;
    if (!ed) return;
    const pos = this.posOf(ed.id);
    ed.el.style.left = `${this.pan.x + (pos.x + NOTE_INSET) * this.zoom}px`;
    ed.el.style.top = `${this.pan.y + (pos.y + NODE_H + NOTE_TOP_GAP) * this.zoom}px`;
    ed.el.style.width = `${Math.max((NODE_W - NOTE_INSET * 2) * this.zoom, 156)}px`;
  }

  private closeNoteEditor(): void {
    if (!this.dropNoteEditor()) return;
    // Restore the panel text now that its editor is gone.
    this.renderNodes();
    this.redrawWires();
    this.highlightSelectedNode();
  }

  /** Remove the editor without repainting, for a caller that draws the board itself next.
   *  Answers whether there was one. */
  private dropNoteEditor(): boolean {
    const ed = this.noteEditor;
    if (!ed) return false;
    this.noteEditor = null;
    ed.el.remove();
    return true;
  }

  /** The host was just made visible: a board still auto-fitting is framed to its real size
   *  now, rather than standing in a fit taken while it had none until the resize callback. */
  shown(): void {
    if (this.autoFit) this.fitView();
  }

  /** Center and scale the diagram to fit the current viewport. */
  fitView(): void {
    const rect = this.svg.getBoundingClientRect();
    const vw = rect.width || 1000;
    // Keep the framed content clear of the hidden-node shelf when it is open.
    const reserved = this.shelf.style.display === "none" ? 0 : this.shelf.offsetHeight;
    const vh = (rect.height || 700) - reserved;
    const b = this.contentBounds();
    const pad = 48;
    const zoom = Math.min(vw / (b.w + pad * 2), vh / (b.h + pad * 2), 1.2);
    this.zoom = Math.max(ZOOM_MIN, zoom);
    this.pan.x = (vw - b.w * this.zoom) / 2 - b.x * this.zoom;
    this.pan.y = (vh - b.h * this.zoom) / 2 - b.y * this.zoom;
    // An intentional fit (construction, model change, hide/show) becomes the new
    // baseline the ResizeObserver keeps in sync until the user pans or zooms.
    this.autoFit = true;
    this.applyTransform();
  }

  // --- scaffold ------------------------------------------------------------

  private buildScaffold(host: HTMLElement): void {
    host.replaceChildren();
    this.host = host;
    this.svg = document.createElementNS(SVGNS, "svg");
    this.svg.setAttribute("width", "100%");
    this.svg.setAttribute("height", "100%");
    this.svg.style.display = "block";
    this.svg.style.touchAction = "none";
    this.svg.setAttribute("role", "group");
    this.svg.setAttribute("aria-label", t().toolbar.viewGraphHint);

    this.svg.append(makeGlowDefs());

    this.viewport = document.createElementNS(SVGNS, "g");
    this.wireLayer = document.createElementNS(SVGNS, "g");
    this.nodeLayer = document.createElementNS(SVGNS, "g");
    this.overlay = document.createElementNS(SVGNS, "g");
    this.cueLayer = document.createElementNS(SVGNS, "g");
    this.cueLayer.classList.add("cue-layer");
    this.cueLayer.style.pointerEvents = "none";
    this.viewport.append(this.wireLayer, this.nodeLayer, this.overlay, this.cueLayer);
    this.svg.append(this.viewport);
    host.append(this.svg);

    // Bottom "spares rack" of hidden nodes; an HTML overlay so it stays out of
    // the SVG export. Empty until something is hidden (renderShelf toggles it).
    this.shelf = document.createElement("div");
    this.shelf.className = "hidden-shelf";
    this.shelf.style.display = "none";
    host.append(this.shelf);

    // Multi-select action bar; an HTML overlay so it stays out of the SVG export.
    // Empty until two or more nodes are selected (renderSelBar toggles it).
    this.selbar = document.createElement("div");
    this.selbar.className = "sel-bar";
    this.selbar.style.display = "none";
    host.append(this.selbar);

    this.svg.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    this.svg.addEventListener("pointermove", (e) => this.onPointerMove(e));
    this.svg.addEventListener("pointerup", (e) => this.onPointerUp(e));
    this.svg.addEventListener("pointercancel", (e) => this.onPointerCancel(e));
    // Losing the window is the fourth way an interaction ends, and the engine announces
    // it with a `blur` and nothing else: measured 2026-08-14 on Chromium and on the
    // shipping WKWebView, the foreground moving away with the button down fires no
    // `pointercancel` and keeps the pointer capture, so a node drag went on writing
    // positions while another application was frontmost — into a second undo entry at
    // that, since history.ts ends its press at the same blur. docs/en/channel-tuning.md,
    // "A gesture the window is taken away from", carries the readings; the CONSOLE and
    // tuning-screen drags end at this event too.
    // Window-lifetime, like the view itself: the app builds one Graph and never takes it
    // down. A suite that builds several takes them back through `recordWindowListeners`.
    window.addEventListener("blur", () => this.endAllPointers());
    this.svg.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });
    this.svg.addEventListener("keydown", (e) => this.onBoardKey(e));
    this.svg.addEventListener("focusin", (e) => {
      const id = this.nodeIdOf(e.target);
      if (id !== null) this.setRovingNode(id);
    });

    // The initial fitView() in the constructor can measure a stale viewport size
    // before the webview has applied its stylesheet/layout (notably WKWebView in
    // the desktop build), leaving the diagram clamped to a tiny zoom. Re-fit once
    // the host gets its real size, and on later resizes, until the user takes over.
    new ResizeObserver(() => {
      if (this.autoFit) this.fitView();
    }).observe(host);
  }

  // --- geometry ------------------------------------------------------------

  private posOf(nodeId: string): Pt {
    // A hung node always derives its position from its parent — just below it — so
    // it moves as a unit and any saved position is ignored. Several nodes can hang
    // on one parent (the SD Rec track slots under their header): each stacks after
    // the earlier visible siblings, so a shelved/inactive slot collapses the rest
    // up. A ducker is its channel's only child, so this adds nothing for it.
    const node = this.nodeById.get(nodeId);
    if (node?.attachTo) {
      const base = this.posOf(node.attachTo);
      let y = base.y + this.nodeHeight(node.attachTo) + DUCKER_GAP;
      for (const sib of this.model.nodes) {
        if (sib.id === nodeId) break;
        if (sib.attachTo === node.attachTo && !this.isHidden(sib.id)) y += this.nodeHeight(sib.id) + DUCKER_GAP;
      }
      return { x: base.x, y };
    }
    const saved = this.plan.positions[nodeId];
    if (saved) return saved;
    return defaultLayoutPos(this.nodeById.get(nodeId)!);
  }

  private parentOf(id: string): string | undefined {
    return this.nodeById.get(id)?.attachTo;
  }

  /** All hung children of a node — a single ducker, or every SD Rec track slot
   *  under the recorder header. They move with the parent as a unit. */
  private attachedDescendants(parentId: string): string[] {
    return this.model.nodes.filter((n) => n.attachTo === parentId).map((n) => n.id);
  }

  private portPoint(r: string): Pt {
    const { nodeId, portId } = parseRef(r);
    const node = this.nodeById.get(nodeId)!;
    const base = this.posOf(nodeId);
    const port = node.ports.find((p) => p.id === portId)!;
    const x = port.direction === "in" ? base.x : base.x + NODE_W;
    return { x, y: base.y + NODE_H / 2 };
  }

  /** The Rec Point tap jack's point on a channel's top edge. */
  private tapPoint(nodeId: string): Pt {
    const base = this.posOf(nodeId);
    return { x: base.x + TAP_CX, y: base.y };
  }

  /** Whether `from → to` leaves the channel at its Rec Point rather than through
   *  the mixer stage — a direct out (USB) or a microSD Rec track. These are the
   *  routes drawn from the tap jack. */
  private isRecPointTap(from: string, to: string): boolean {
    return directOutTarget(this.model, from, to) !== null;
  }

  /** Whether a node needs a Rec Point tap jack — read from the routes that would
   *  leave it, not from its kind. Asking the same predicate that decides where a
   *  wire is drawn keeps the jack and its wires from ever disagreeing: a node with
   *  such a route always has the jack the wire rises out of, and one without never
   *  shows a jack nothing can leave. Today that is exactly the channels. */
  private hasTapJack(node: DeviceNode): boolean {
    const out = ref(node.id, "out");
    return [...possibleTargets(this.model, out)].some((r) => this.isRecPointTap(out, r));
  }

  /** The jack that represents `sourceRef` for a wire into `targetRef`: a channel's
   *  Rec Point tap for a direct out / recording, its right-edge output otherwise. */
  private sourceJack(sourceRef: string, targetRef: string): SVGCircleElement | undefined {
    return this.isRecPointTap(sourceRef, targetRef)
      ? this.tapEls.get(parseRef(sourceRef).nodeId)
      : this.portEls.get(sourceRef);
  }

  private clientToContent(e: PointerEvent): Pt {
    const rect = this.svg.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - this.pan.x) / this.zoom,
      y: (e.clientY - rect.top - this.pan.y) / this.zoom,
    };
  }

  private applyTransform(): void {
    this.viewport.setAttribute("transform", `translate(${this.pan.x} ${this.pan.y}) scale(${this.zoom})`);
    this.positionNoteEditor();
  }

  // --- rendering -----------------------------------------------------------

  render(): void {
    this.nodeById.clear();
    for (const node of this.model.nodes) this.nodeById.set(node.id, node);
    // An open note editor stays open, focus and any IME composition with it, for as long as
    // its node is drawn; applyTransform below puts it back over the node's panel.
    const ed = this.noteEditor;
    if (ed && (!this.nodeById.has(ed.id) || this.isHidden(ed.id))) this.dropNoteEditor();
    this.applyTransform();
    this.renderNodes();
    // redrawWires ends with refreshPortStates, so the port glow is restored there.
    this.redrawWires();
    this.renderShelf();
    this.highlightSelectedNode();
    this.renderSelBar();
  }

  /** Repaint the i18n-bearing chrome with the current messages, leaving the hidden
   *  set and selection untouched (called on a language switch): the shelf and multi-
   *  select bar, plus the node/wire layers, whose SVG tooltips and wire <title>s
   *  bake the language in at paint time. */
  relocalizeChrome(): void {
    this.svg.setAttribute("aria-label", t().toolbar.viewGraphHint);
    this.renderNodes();
    this.redrawWires();
    this.renderShelf();
    this.highlightSelectedNode();
    this.renderSelBar();
  }

  private renderNodes(): void {
    const focused = this.focusedNodeId();
    this.drawnGated = this.gatedSlots();
    this.nodeLayer.replaceChildren();
    this.nodeEls.clear();
    this.portEls.clear();
    this.portPinEls.clear();
    this.tapEls.clear();
    this.tapPinEls.clear();
    for (const node of this.model.nodes) {
      if (this.isHidden(node.id)) continue;
      this.nodeLayer.append(this.makeNode(node));
    }
    // STEREO-link ties: a heart connector between the two nodes of a STEREO-linked
    // MONO IN pair (Signal Type), mirroring the device's linked-channel look.
    this.stereoLinkEls.clear();
    for (const [a, b] of this.model.channelPairs) {
      if (!this.plan.nodeParams[a]?.stereoLink) continue;
      if (this.isHidden(a) || this.isHidden(b)) continue;
      this.redrawStereoLink(a, b);
    }
    this.syncRovingNode();
    this.refocusNode(focused);
    this.builtFor = this.plan;
    this.repaintCandidates();
  }

  // --- keyboard ------------------------------------------------------------

  /** The id of the board node `target` is, or sits in; null for anything else. */
  private nodeIdOf(target: EventTarget | null): string | null {
    const g = target instanceof Element ? target.closest<SVGGElement>("g.node") : null;
    return g && this.nodeLayer.contains(g) ? (g.dataset.id ?? null) : null;
  }

  /** The board node keyboard focus is on, if it is on one. */
  private focusedNodeId(): string | null {
    return this.nodeIdOf(document.activeElement);
  }

  /** Put focus back on the node drawn for `id` after its element was replaced or moved,
   *  for the plan the board was built for only. */
  private refocusNode(id: string | null): void {
    if (id === null || this.plan !== this.builtFor) return;
    this.nodeEls.get(id)?.focus({ preventScroll: true });
  }

  private setRovingNode(id: string): void {
    this.focusNode = id;
    this.syncRovingNode();
  }

  /** One node of the board is in the tab order at a time. */
  private syncRovingNode(): void {
    const pick = this.rovingId();
    for (const [id, el] of this.nodeEls) el.setAttribute("tabindex", id === pick ? "0" : "-1");
  }

  /** The node the board puts in the tab order. */
  private rovingId(): string | null {
    const anchor = this.selection?.type === "node" ? this.selection.id : null;
    return (
      [this.focusNode, anchor].find((id): id is string => id !== null && this.nodeEls.has(id)) ??
      this.nodeEls.keys().next().value ??
      null
    );
  }

  /** Rebuild the shelf or the selection bar with `build`, handing keyboard focus on one of
   *  its buttons to the same button afterwards — a chip by the node it restores, Show all
   *  and the bar's buttons by their class — for as long as the plan is the one it was last
   *  built for. A button the rebuild no longer offers takes the focus with it. */
  private rebuildChrome(bar: HTMLElement, build: () => void): void {
    const keyOf = (el: HTMLElement): string | null =>
      el.dataset.node ? `node:${el.dataset.node}` : el.className || null;
    const restore = preserveFocus(bar, keyOf, (key) =>
      [...bar.querySelectorAll<HTMLElement>("button")].find((b) => keyOf(b) === key),
    );
    build();
    if (this.chromeBuiltFor.get(bar) === this.plan) restore();
    this.chromeBuiltFor.set(bar, this.plan);
  }

  /** Enter / Space select the focused node as a press does; the arrow keys walk focus through
   *  the drawn nodes in the model's order (Right / Down onward, Left / Up back, wrapping at the
   *  ends) and pan the one reached into view. */
  private onBoardKey(e: KeyboardEvent): void {
    const id = this.nodeIdOf(e.target);
    if (id === null || isChord(e)) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.select({ type: "node", id });
      return;
    }
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const ids = [...this.nodeEls.keys()];
    const next = ids[(ids.indexOf(id) + step + ids.length) % ids.length];
    this.setRovingNode(next);
    this.nodeEls.get(next)?.focus({ preventScroll: true });
    this.panIntoView(next, []);
  }

  /** Re-light the connect-drag candidates after every port element has been replaced.
   *  Their highlight lives only in those elements, so a redraw landing mid-drag —
   *  a device-follow reflect, a repaint — left the operator finishing the connection
   *  with no legality cues. */
  private repaintCandidates(): void {
    const c = this.connect;
    if (c?.mode !== "connecting") return;
    const { legal, possible } = this.connectCandidates(c.ref, c.dir, c.tap);
    this.paintCandidates(c.ref, c.dir, legal, possible);
  }

  /** Snap a pair's partner next to the kept node when STEREO-linking, so the heart
   *  tie isn't stretched across a gap left by an earlier manual move. The selected
   *  member stays put; the other moves to its canonical relative offset. */
  alignStereoPair(primary: string): void {
    const partner = partnerChannel(this.model, primary);
    if (!partner) return;
    const keepPartner = this.selection?.type === "node" && this.selection.id === partner;
    const kept = keepPartner ? partner : primary;
    const other = keepPartner ? primary : partner;
    this.plan.positions[other] = this.alignedPairPos(kept, other);
  }

  /** Where a pair's `other` member belongs beside the one kept in place: the offset the
   *  default layout gives the two, carried onto the kept node's position. Channels are never
   *  hung, so their drawn position is the saved one or the default. */
  private alignedPairPos(kept: string, other: string): Pt {
    const dk = defaultLayoutPos(this.nodeById.get(kept)!);
    const dother = defaultLayoutPos(this.nodeById.get(other)!);
    const kp = this.plan.positions[kept] ?? dk;
    return { x: kp.x + (dother.x - dk.x), y: kp.y + (dother.y - dk.y) };
  }

  /** Whether `other` already stands in the pair's slot beside `kept`: its own column, on the
   *  side the layout puts it, no closer than the default offset and no further than the rows
   *  the upper of the two occupies.
   *
   *  A BAND rather than the one position `alignedPairPos` writes, because Arrange advances a
   *  column by whole rows big enough to clear an expanded note (`rowsFor`, shared with it) and
   *  a load must not undo that. Widening the written offset instead is not the answer: Arrange
   *  re-packs the whole column while this moves two nodes, so the pair would land on top of
   *  whatever sits in the row it grew into. The band's own floor is the default offset, so a
   *  pair the operator stretched still comes back.
   *
   *  Its lower edge carries the tolerance for the same reason its equality once did: a dragged
   *  pair's saved offset is the default one re-added at a different base, which lands ~1e-13 px
   *  off often enough to matter, and a write nobody can see is still a plan write. */
  private inPairSlot(kept: string, other: string): boolean {
    const want = this.alignedPairPos(kept, other);
    const kp = this.posOf(kept);
    const at = this.posOf(other);
    if (Math.abs(at.x - want.x) >= ALIGN_SLOP) return false;
    const wantDy = want.y - kp.y;
    const dy = at.y - kp.y;
    if (Math.sign(dy) !== Math.sign(wantDy)) return false;
    const clear = this.rowsFor(wantDy > 0 ? kept : other) * ROW_GAP;
    return Math.abs(dy) >= Math.abs(wantDy) - ALIGN_SLOP && Math.abs(dy) <= clear + ALIGN_SLOP;
  }

  /** How many whole rows a node occupies — its own drawn height plus any hung children,
   *  rounded up to the grid. Arrange advances a column by this, so a position derived from
   *  it lands where Arrange would put the node below. */
  private rowsFor(id: string): number {
    let span = this.nodeHeight(id);
    for (const childId of this.attachedDescendants(id))
      if (!this.isHidden(childId)) span += DUCKER_GAP + this.nodeHeight(childId);
    return Math.max(1, Math.ceil((span + (ROW_GAP - NODE_H)) / ROW_GAP));
  }

  /** Snap the partner of every STEREO-linked pair to its canonical offset from the pair's
   *  primary, and report whether any of them moved.
   *
   *  What `alignStereoPair` does for a link made in the app, for the links that arrive with
   *  no edit funnel behind them: a device read, a device-follow reconcile of a Signal Type
   *  moved on the unit's own panel, and a loaded document. Without it the heart tie is drawn
   *  across whatever gap an earlier manual move opened.
   *
   *  The primary is kept rather than the selected member, so the pair settles on the same two
   *  slots whatever the board's selection is. A pair with either member shelved is left alone:
   *  no tie is drawn for it, and snapping the visible member toward a shelved one would
   *  relocate it for a partner that is not on screen. */
  alignLinkedPairs(): boolean {
    let moved = false;
    for (const [primary, partner] of this.model.channelPairs) {
      if (!this.plan.nodeParams[primary]?.stereoLink) continue;
      if (this.isHidden(primary) || this.isHidden(partner)) continue;
      // Against the partner's DRAWN position, not its saved one: a pair neither member of
      // which was ever moved is already in its slot, and writing a position for it would
      // put an entry in the document for a node nothing relocated.
      if (this.inPairSlot(primary, partner)) continue;
      this.plan.positions[partner] = this.alignedPairPos(primary, partner);
      moved = true;
    }
    return moved;
  }

  /** Snap `id` to its canonical offset from a STEREO-linked partner already on the board,
   *  and report whether it did. The node already there stays where it is. */
  private snapToLinkedPartner(id: string): void {
    const partner = this.linkedPartnerOnBoard(id);
    // Not unconditionally: a node whose slot is already the right one gets no document entry
    // and no undo entry for a move of zero pixels.
    if (partner && !this.inPairSlot(partner, id)) this.plan.positions[id] = this.alignedPairPos(partner, id);
  }

  /** The STEREO-linked partner of `id` that is on the board, or null — which is what says
   *  whether there is a slot to snap into at all, separately from whether `id` already
   *  stands in it. */
  private linkedPartnerOnBoard(id: string): string | null {
    const pair = this.linkedPairOf(id);
    const partner = pair ? (pair[0] === id ? pair[1] : pair[0]) : null;
    return partner && !this.isHidden(partner) ? partner : null;
  }

  /** The channelPairs entry [primary, partner] containing `id` when that pair is
   *  STEREO-linked, else null. Used to drag a linked pair as one unit. */
  private linkedPairOf(id: string): [string, string] | null {
    const a = pairPrimary(this.model, id);
    if (!a || !this.plan.nodeParams[a]?.stereoLink) return null;
    return [a, partnerChannel(this.model, a)!];
  }

  // Build (or rebuild) the heart tie for a pair, keyed by its primary `a`, and put
  // it in the node layer — replacing any existing tie. Shared by the full repaint
  // and the live drag so the two stay in sync.
  private redrawStereoLink(a: string, b: string): void {
    const tie = this.makeStereoLink(a, b);
    const old = this.stereoLinkEls.get(a);
    if (old) old.replaceWith(tie);
    else this.nodeLayer.append(tie);
    this.stereoLinkEls.set(a, tie);
  }

  // A decorative heart tie drawn in the gap between a STEREO-linked pair, anchored
  // bottom-center of the upper node to top-center of the lower one.
  private makeStereoLink(a: string, b: string): SVGGElement {
    const pa = this.posOf(a);
    const pb = this.posOf(b);
    const [up, lo] = pa.y <= pb.y ? [pa, pb] : [pb, pa];
    const x1 = up.x + NODE_W / 2;
    const y1 = up.y + NODE_H;
    const x2 = lo.x + NODE_W / 2;
    const y2 = lo.y;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const accent = this.palette.rail.channel;
    const g = document.createElementNS(SVGNS, "g");
    g.style.pointerEvents = "none";
    g.append(svgLine(x1, y1, x2, y2, accent, 2, 0.85));
    const disc = document.createElementNS(SVGNS, "circle");
    disc.setAttribute("cx", String(mx));
    disc.setAttribute("cy", String(my));
    disc.setAttribute("r", "8");
    disc.setAttribute("fill", this.palette.nodeFill);
    disc.setAttribute("stroke", accent);
    disc.setAttribute("stroke-width", "1");
    g.append(disc);
    const heart = document.createElementNS(SVGNS, "text");
    heart.setAttribute("x", String(mx));
    heart.setAttribute("y", String(my));
    heart.setAttribute("text-anchor", "middle");
    heart.setAttribute("dominant-baseline", "central");
    heart.setAttribute("font-size", "10");
    heart.setAttribute("fill", accent);
    heart.textContent = "♥";
    g.append(heart);
    return g;
  }

  // A node is hidden whenever it is shelved; its wires — fixed or editable — are
  // hidden along with it (see redrawWires). A hung child is also hidden whenever
  // its parent is, so a ducker is never shown without its channel.
  private isHidden(id: string): boolean {
    const parent = this.parentOf(id);
    if (parent && this.isHidden(parent)) return true;
    if (this.sdRecSlotInactive(id)) return true;
    return this.hidden.has(id);
  }

  /** A structural child slot (hung under a header — a microSD Rec track slot). It
   *  belongs to the recorder, so "hide unused" leaves it for the header to manage
   *  rather than shelving it loose; the user may still shelve one by hand (it gets
   *  a chip like a ducker). */
  private isStructuralSlot(id: string): boolean {
    return hangsUnderHeader(this.model, id);
  }

  // A microSD Rec track-pair slot beyond the current Track Count is inactive — the
  // device records only Track Count tracks — so it is not drawn. The count is
  // read-only on the device; the planner edits it on the SD Rec header node.
  private sdRecSlotInactive(id: string): boolean {
    const m = /^out\.sdrec\.t(\d+)$/.exec(id);
    if (!m) return false;
    const stored = this.plan.nodeParams["out.sdrec"]?.sdRecTrackCount ?? SD_REC_TRACK_COUNT_DEFAULT;
    // Against the RATE's ceiling as well. setPlanSampleRate already lowers the stored value,
    // so the two normally agree; drawing from the ceiling too means a plan that arrived any
    // other way cannot offer a slot to wire that the unit has no track for.
    const count = Math.min(stored, trackCountCeiling(this.plan.sampleRate));
    return Number(m[1]) > Math.floor(count / 2);
  }

  // Whether a node is an endpoint of any wire (fixed sends included).
  private nodeHasWire(id: string): boolean {
    return this.plan.connections.some((c) => parseRef(c.from).nodeId === id || parseRef(c.to).nodeId === id);
  }

  // Whether a connection is an inaudible send: switched off (params.on === false —
  // a CH/FX send or the MIX → STEREO "TO ST"), bound to an inactive node (either
  // endpoint muted / bypassed — a muted channel, a bus/FX/MONITOR master OFF, or a
  // bypassed ducker on its key wire), or a `send` whose level sits at -∞
  // (≤ LEVEL_MIN_DB). Every CH/FX send and the TO ST are fixed now, so this — not
  // wire presence — is what marks one silent. The main fader paths (CH/FX → STEREO)
  // carry no `on` and default to unity, so they only read off when pulled to -∞.
  // OSC → bus carries oscL/oscR (per-bus assign), so its wire also reads off when
  // both its L and R assigns are off — and a muted endpoint (incl. the oscillator
  // off, via isNodeInactive) recedes it too, the same uniform rule every node follows.
  // A wire to or from a node the sample rate has taken away (FX2 above 96 kHz) carries
  // nothing whatever its params hold, so it recedes as well. A channel's mute sits at its
  // ON switch, after its Rec Point: a direct out or a channel key leaving a muted channel
  // stays live, and so does the wire into it while it has such a live output.
  private isOffSend(conn: PlanConnection): boolean {
    if (conn.params?.on === false) return true;
    const fromId = parseRef(conn.from).nodeId;
    const toId = parseRef(conn.to).nodeId;
    const rate = this.plan.sampleRate;
    if (nodeRateDisabled(fromId, rate) || nodeRateDisabled(toId, rate)) return true;
    const src = this.nodeById.get(fromId);
    const dst = this.nodeById.get(toId);
    const srcSilent =
      src !== undefined &&
      this.isNodeInactive(src) &&
      !(src.kind === "channel" && channelTapAheadOfMute(this.model, conn.from, conn.to));
    const dstSilent =
      dst !== undefined && this.isNodeInactive(dst) && !(dst.kind === "channel" && this.hasLiveTapAheadOfMute(toId));
    if (srcSilent || dstSilent) return true;
    // OSC → bus assign: silent when its L (and R, on a stereo bus) are off, even
    // while the oscillator runs. A mono FX bus uses L only (r === null).
    if (fromId === "bus.osc") {
      const a = oscAssign(toId);
      const rOff = !a || a.r === null || conn.params?.oscR === false;
      return conn.params?.oscL === false && rOff;
    }
    if (conn.kind !== "send") return false;
    // A FIXED MIX bus takes the send at a fixed level whatever the plan's level holds,
    // so -∞ there silences nothing; only the send's ON (checked above) can.
    if (mixSendLocks(this.plan, toId).busFixed) return false;
    return (conn.params?.level ?? 0) <= LEVEL_MIN_DB;
  }

  // Whether a channel has a live wire leaving it ahead of its mute (a direct out or a key).
  private hasLiveTapAheadOfMute(channelId: string): boolean {
    return this.plan.connections.some(
      (c) =>
        parseRef(c.from).nodeId === channelId && channelTapAheadOfMute(this.model, c.from, c.to) && !this.isOffSend(c),
    );
  }

  // Whether a node reads as inactive and should be dimmed. Delegates to the shared
  // core predicate so the CONSOLE view dims the same nodes by construction.
  private isNodeInactive(node: DeviceNode): boolean {
    return isNodeInactive(this.plan, node);
  }

  // Resting opacity of a node's faceplate, applied by highlightSelectedNode (the
  // single source) by the same precedence makeNode badges a node's state
  // (rate-disabled > inactive > unread > plain). A path trace fades the off-path
  // nodes to a fraction of this, so the dim stays derived from state, not stored.
  private restingOpacity(node: DeviceNode): number {
    if (this.disabledNodes.has(node.id)) return 0.62;
    if (this.isNodeInactive(node)) return 0.4;
    if (this.unreadNodes.has(node.id)) return 0.7;
    return 1;
  }

  /** The device CH SETTING name (plan.nodeNames) to show for a node, or undefined
   *  in model-label mode or when no name is set — the single source both the
   *  faceplate and labelOf use to decide a name override. */
  private deviceName(id: string): string | undefined {
    return this.labelSource === "device" ? this.plan.nodeNames?.[id]?.trim() || undefined : undefined;
  }

  /** Full display name (both label tiers) for status lines. In device mode the
   *  CH SETTING name wins over the model's label; in model mode the planner label
   *  always shows. (The shelf builds its chips from fullLabel directly, so those
   *  ignore device-name mode.) Public so every status line that names a node — the
   *  graph's own and the undo history's — prints what the canvas prints. */
  labelOf(id: string): string {
    const custom = this.deviceName(id);
    if (custom) return custom;
    const node = this.nodeById.get(id);
    return node ? fullLabel(node) : id;
  }

  private makeNode(node: DeviceNode): SVGGElement {
    const p = this.palette;
    const rail = p.rail[node.kind];
    const g = document.createElementNS(SVGNS, "g");
    g.classList.add("node");
    g.dataset.id = node.id;
    g.setAttribute("role", "button");
    g.setAttribute("aria-label", spoken(this.labelOf(node.id)));
    g.setAttribute("aria-pressed", String(this.selectedNodes.has(node.id)));
    g.setAttribute("tabindex", "-1");
    const pos = this.posOf(node.id);
    g.setAttribute("transform", `translate(${pos.x} ${pos.y})`);

    // A hung node (ducker) shows a thin tether up into the gap to its parent, so
    // the two read as one unit. Inline attributes keep it in the PNG/PDF export.
    if (node.attachTo) g.append(svgLine(NODE_W / 2, -DUCKER_GAP, NODE_W / 2, 0, rail, 2, 0.5));

    // A note expands the node downward into an in-frame panel; the header keeps
    // its fixed height so jacks, label and wires stay anchored to the top.
    const lines = this.noteLines(node.id);
    const editing = this.noteEditor?.id === node.id;
    const showPanel = lines.length > 0 && (!this.collapsed.has(node.id) || editing);
    const h = NODE_H + (showPanel ? notePanelHeight(lines) : 0);

    const rect = document.createElementNS(SVGNS, "rect");
    rect.setAttribute("width", String(NODE_W));
    rect.setAttribute("height", String(h));
    rect.setAttribute("rx", "7");
    rect.setAttribute("fill", p.nodeFill);
    rect.setAttribute("stroke", p.nodeStroke);
    rect.setAttribute("stroke-width", "1");
    if (this.themeName === "light") rect.setAttribute("filter", "url(#node-shadow)");
    g.append(rect);

    const hi = svgRect(1, 1, NODE_W - 2, 13, 5, p.nodeHi);
    g.append(hi);

    const railEl = svgRect(0, 0, 6, h, 2, rail);
    g.append(railEl);

    // User color override (plan.nodeColors): a thin accent cap along the top
    // edge. Keeps the kind rail intact, so the cap is purely additional.
    const capColor = planColorHex(this.plan.nodeColors?.[node.id]);
    if (capColor) g.append(svgRect(6, 0, NODE_W - 6, 3, 1.5, capColor));

    for (const sx of [12, NODE_W - 12]) {
      for (const sy of [9, h - 9]) {
        const screw = document.createElementNS(SVGNS, "circle");
        screw.setAttribute("cx", String(sx));
        screw.setAttribute("cy", String(sy));
        screw.setAttribute("r", "2.2");
        screw.setAttribute("fill", p.screwFill);
        screw.setAttribute("stroke", p.screwStroke);
        screw.setAttribute("stroke-width", "0.8");
        screw.style.pointerEvents = "none";
        g.append(screw);
      }
    }

    // Device mode shows the CH SETTING name (plan.nodeNames) in place of the
    // model's primary label; model mode keeps the planner label. The dim
    // sublabel legend (if any) stays as secondary context either way.
    const primary = this.deviceName(node.id) ?? node.label;
    if (node.sublabel) {
      // Two-tier faceplate label: the node name, then a dim secondary legend
      // beneath it, so a long name fits the fixed header height.
      const s1 = fitScale(primary, LABEL_FS, 1, LABEL_MAX_W);
      g.append(labelText(primary, LABEL_TIER1_Y, LABEL_FS * s1, s1, p.label, 1));
      const s2 = fitScale(node.sublabel, LABEL_SUB_FS, 0.5, LABEL_MAX_W);
      g.append(labelText(node.sublabel, LABEL_TIER2_Y, LABEL_SUB_FS * s2, 0.5 * s2, p.label, p.sublabelOpacity));
    } else {
      // Single line, scaled down only when it would otherwise run under the button.
      const s = fitScale(primary, LABEL_FS, 1, LABEL_MAX_W);
      g.append(labelText(primary, NODE_H / 2 + 1, LABEL_FS * s, s, p.label, 1));
    }

    // A header node (microSD Rec) takes no direct wire — its I/O lives on the
    // child slots — so its port connector is not drawn.
    for (const port of node.header ? [] : node.ports) {
      const cx = port.direction === "in" ? 0 : NODE_W;
      const r = ref(node.id, port.id);

      // Oversized transparent disc that actually receives the pointer; the
      // class/ref the handlers and elementFromPoint look up live here, while
      // the visible jack below stays decorative. Kept first so the painted
      // jack and pin (pointer-events: none) sit on top without blocking it.
      const hit = document.createElementNS(SVGNS, "circle");
      hit.classList.add("port", "port-hit", port.direction === "in" ? "port-in" : "port-out");
      hit.dataset.ref = r;
      hit.setAttribute("cx", String(cx));
      hit.setAttribute("cy", String(NODE_H / 2));
      hit.setAttribute("r", String(PORT_HIT_R));
      hit.setAttribute("fill", "transparent");
      hit.style.pointerEvents = "all";
      g.append(hit);

      const { jack, pin } = jackPair(cx, NODE_H / 2, rail, p);
      // Marks the painted jack (not its hit disc) for tests and DOM inspection;
      // the Rec Point tap deliberately has no counterpart, since it is identified
      // by its port-tap class.
      jack.dataset.dir = port.direction;
      pin.dataset.pin = r;
      g.append(jack, pin);
      this.portEls.set(r, jack);
      this.portPinEls.set(r, pin);
    }

    // The channel's Rec Point tap, on the top edge: direct outs and recordings
    // leave here, ahead of the fader and Ducker that the right-edge output feeds.
    // Every channel shows one so the tap is findable before anything is wired.
    if (this.hasTapJack(node)) g.append(this.makeTapJack(node, rail, p));

    // A node may read as inactive (muted / bypassed / osc-off), rate-disabled or
    // unread-from-device at once; show only the highest-ranked state so the badges
    // and frames never collide. Precedence: rate-disabled > inactive > unread —
    // a feature unusable at this rate dominates a user mute, which dominates a
    // mere provenance warning.
    const badges: SVGElement[] = [];
    if (this.disabledNodes.has(node.id)) {
      badges.push(svgRect(NODE_W - 34, -8, 30, 15, 3, p.warn));
      const badge = document.createElementNS(SVGNS, "text");
      badge.setAttribute("x", String(NODE_W - 19));
      badge.setAttribute("y", "0");
      badge.setAttribute("text-anchor", "middle");
      badge.setAttribute("dominant-baseline", "central");
      badge.setAttribute("fill", p.warnInk);
      badge.setAttribute("font-family", LABEL_FONT);
      badge.setAttribute("font-size", "8.5");
      badge.setAttribute("font-weight", "700");
      badge.style.pointerEvents = "none";
      badge.textContent = "OFF";
      badges.push(badge);
    } else if (this.isNodeInactive(node)) {
      // A muted node (CH_ON off) / bypassed ducker (duckerOn off) / oscillator off
      // (osc.on): tag it MUTE (a mute) or OFF (a ducker / the oscillator, whose
      // resting state is off, not muted). highlightSelectedNode does the dimming.
      const tag = svgRect(NODE_W - 40, -8, 36, 15, 3, p.nodeStroke);
      g.append(tag);
      const tagText = document.createElementNS(SVGNS, "text");
      tagText.setAttribute("x", String(NODE_W - 22));
      tagText.setAttribute("y", "0");
      tagText.setAttribute("text-anchor", "middle");
      tagText.setAttribute("dominant-baseline", "central");
      tagText.setAttribute("fill", p.label);
      tagText.setAttribute("font-family", LABEL_FONT);
      tagText.setAttribute("font-size", "8.5");
      tagText.setAttribute("font-weight", "700");
      tagText.style.pointerEvents = "none";
      tagText.textContent = node.kind === "ducker" || node.id === "bus.osc" ? "OFF" : "MUTE";
      g.append(tagText);
    }

    // A node still showing its plan default after a device readback (not confirmed
    // by the device): a dim node with a dashed warn frame and a top-left "?" badge.
    // Ranks below MUTE and DISABLED (the else-if keeps it from stacking), so the
    // badge sits alone in the top-left, never colliding with their top-right tags.
    else if (this.unreadNodes.has(node.id)) {
      badges.push(svgRect(8, -8, 16, 15, 3, p.warn));
      const badge = document.createElementNS(SVGNS, "text");
      badge.setAttribute("x", "16");
      badge.setAttribute("y", "0");
      badge.setAttribute("text-anchor", "middle");
      badge.setAttribute("dominant-baseline", "central");
      badge.setAttribute("fill", p.warnInk);
      badge.setAttribute("font-family", LABEL_FONT);
      badge.setAttribute("font-size", "9");
      badge.setAttribute("font-weight", "700");
      badge.style.pointerEvents = "none";
      badge.textContent = "?";
      badges.push(badge);
    }

    if (lines.length) {
      g.append(this.makeNoteToggle(node, this.collapsed.has(node.id) && !editing));
      // Draw the panel when expanded; its text is hidden while the inline editor
      // stands in its place (the well + seam stay so the frame reads as a panel).
      if (showPanel) g.append(this.makeNotePanel(node, lines, h, !editing));
    } else {
      // No note yet: the pen button is the way in (double-click now traces the
      // signal path instead of opening the note editor).
      g.append(this.makeNoteAdd(node));
    }

    // The OFF / "?" badge stays at full strength while its node dims: the node's body
    // goes into a group of its own, which highlightSelectedNode dims instead of the node.
    if (badges.length) {
      const body = document.createElementNS(SVGNS, "g");
      body.classList.add("node-body");
      body.append(...g.childNodes);
      const badge = document.createElementNS(SVGNS, "g");
      badge.classList.add("node-badge");
      badge.append(...badges);
      g.append(body, badge);
    }

    // The keyboard focus ring, outside the body a badge splits off and so clear of its dim:
    // stroked by the stylesheet only while the node holds :focus-visible, and stroke-less in
    // an export, which carries no stylesheet.
    const ring = document.createElementNS(SVGNS, "rect");
    ring.classList.add("focus-ring");
    ring.setAttribute("x", "-4");
    ring.setAttribute("y", "-4");
    ring.setAttribute("width", String(NODE_W + 8));
    ring.setAttribute("height", String(h + 8));
    ring.setAttribute("rx", "10");
    ring.setAttribute("fill", "none");
    ring.setAttribute("stroke", "none");
    ring.style.pointerEvents = "none";
    g.append(ring);

    this.nodeEls.set(node.id, g);
    return g;
  }

  /** A channel's Rec Point tap jack: the pickup for direct outs and recordings,
   *  taken ahead of the fader and Ducker. Built like the side ports — an oversized
   *  transparent hit disc under a decorative jack and pin — but keyed by node id,
   *  since it shares the channel's `ch:out` ref. A <title> names the stage in the
   *  same words the inspector uses on the wires that leave here. */
  private makeTapJack(node: DeviceNode, rail: string, p: Palette): SVGGElement {
    const g = document.createElementNS(SVGNS, "g");
    const title = document.createElementNS(SVGNS, "title");
    title.textContent = t().tooltip.recPointTap;
    g.append(title);

    // Unlike the side ports, the tap's target reaches mostly upward, into the
    // gutter above the strip, rather than a disc centered on the jack: the top
    // edge is where a node is grabbed to drag it, and a full-size disc there
    // would swallow presses meant for the faceplate.
    const hit = document.createElementNS(SVGNS, "rect");
    // port-out lets the release hit-test find it like any other source jack; the
    // extra port-tap class (and data-tap, not data-ref) tells the two apart
    // without giving one ref two elements under a [data-ref] lookup.
    hit.classList.add("port", "port-hit", "port-out", "port-tap");
    hit.dataset.tap = ref(node.id, "out");
    hit.setAttribute("x", String(TAP_CX - TAP_HIT_W / 2));
    hit.setAttribute("y", String(-TAP_HIT_UP));
    hit.setAttribute("width", String(TAP_HIT_W));
    hit.setAttribute("height", String(TAP_HIT_UP + TAP_HIT_DOWN));
    hit.setAttribute("fill", "transparent");
    hit.style.pointerEvents = "all";
    g.append(hit);

    const { jack, pin } = jackPair(TAP_CX, 0, rail, p);
    pin.dataset.tapPin = node.id;
    g.append(jack, pin);
    this.tapEls.set(node.id, jack);
    this.tapPinEls.set(node.id, pin);
    return g;
  }

  /** Full node height including an expanded in-frame note panel. */
  private nodeHeight(id: string): number {
    const lines = this.noteLines(id);
    const editing = this.noteEditor?.id === id;
    const showPanel = lines.length > 0 && (!this.collapsed.has(id) || editing);
    return NODE_H + (showPanel ? notePanelHeight(lines) : 0);
  }

  /** Clipped, wrapped lines for a node's note, or [] when it carries none. */
  private noteLines(id: string): string[] {
    return clipNote(this.plan.notes?.[id] ?? "");
  }

  // Header control center, kept left of the right-edge jack hit zone.
  private static readonly BTN_CX = NODE_W - 25;

  /** A small bordered header button (the visible affordance) with a padded hit
   *  area; `draw` paints the icon glyph centered in it. */
  private makeHeaderButton(
    node: DeviceNode,
    cls: string,
    tip: string,
    draw: (cx: number, cy: number, ink: string) => SVGElement[],
  ): SVGGElement {
    const cx = Graph.BTN_CX;
    const cy = NODE_H / 2;
    const g = document.createElementNS(SVGNS, "g");
    g.classList.add(cls);
    g.style.cursor = "pointer";
    const title = document.createElementNS(SVGNS, "title");
    title.textContent = tip;
    g.append(title);

    const hit = document.createElementNS(SVGNS, "rect");
    hit.setAttribute("x", String(cx - 10));
    hit.setAttribute("y", String(cy - 9));
    hit.setAttribute("width", "20");
    hit.setAttribute("height", "18");
    hit.setAttribute("fill", "transparent");
    hit.style.pointerEvents = "all";
    g.append(hit);

    const btn = document.createElementNS(SVGNS, "rect");
    btn.classList.add("note-btn");
    btn.setAttribute("x", String(cx - 8));
    btn.setAttribute("y", String(cy - 7));
    btn.setAttribute("width", "16");
    btn.setAttribute("height", "14");
    btn.setAttribute("rx", "3.5");
    btn.setAttribute("fill", this.themeName === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.05)");
    btn.setAttribute("stroke", this.palette.rail[node.kind]);
    btn.setAttribute("stroke-width", "1");
    btn.style.pointerEvents = "none";
    g.append(btn);

    for (const el of draw(cx, cy, this.palette.label)) {
      el.style.pointerEvents = "none";
      g.append(el);
    }
    return g;
  }

  /** Button that minimizes / re-expands the in-frame note panel (− / +). */
  private makeNoteToggle(node: DeviceNode, collapsed: boolean): SVGGElement {
    const m = collapsed ? t().tooltip.expandNote : t().tooltip.collapseNote;
    return this.makeHeaderButton(node, "note-toggle", m, (cx, cy, ink) => {
      const bar = (d: string): SVGPathElement => {
        const el = document.createElementNS(SVGNS, "path");
        el.setAttribute("d", d);
        el.setAttribute("stroke", ink);
        el.setAttribute("stroke-width", "1.8");
        el.setAttribute("stroke-linecap", "round");
        return el;
      };
      const els = [bar(`M ${cx - 3.5} ${cy} L ${cx + 3.5} ${cy}`)];
      // Plus while collapsed (expand action), minus while expanded (collapse).
      if (collapsed) els.push(bar(`M ${cx} ${cy - 3.5} L ${cx} ${cy + 3.5}`));
      return els;
    });
  }

  /** Button that opens the editor on a note-less node (a pen glyph). Its resting opacity
   *  is a presentation attribute as well as a CSS rule: the export clone carries no
   *  stylesheet, and the CSS states (hover, selected) outrank the attribute on screen. */
  private makeNoteAdd(node: DeviceNode): SVGGElement {
    const g = this.makeHeaderButton(node, "note-add", t().tooltip.addNote, (cx, cy, ink) => {
      const shaft = document.createElementNS(SVGNS, "path");
      shaft.setAttribute("d", `M ${cx - 4} ${cy + 4} L ${cx + 2} ${cy - 2}`);
      shaft.setAttribute("stroke", ink);
      shaft.setAttribute("stroke-width", "2.2");
      shaft.setAttribute("stroke-linecap", "round");
      const tip = document.createElementNS(SVGNS, "path");
      tip.setAttribute("d", `M ${cx + 2} ${cy - 2} L ${cx + 4.5} ${cy - 4.5}`);
      tip.setAttribute("stroke", ink);
      tip.setAttribute("stroke-width", "1.3");
      tip.setAttribute("stroke-linecap", "round");
      return [shaft, tip];
    });
    g.setAttribute("opacity", "0.42");
    return g;
  }

  /** The in-frame note area below the header: recessed well, seam and text. */
  private makeNotePanel(node: DeviceNode, lines: string[], h: number, showText: boolean): SVGGElement {
    const p = this.palette;
    const seamY = NODE_H;
    const wellY = NODE_H + NOTE_TOP_GAP;
    const g = document.createElementNS(SVGNS, "g");
    g.classList.add("note-panel");
    g.style.pointerEvents = "none";

    const well = document.createElementNS(SVGNS, "rect");
    well.setAttribute("x", String(NOTE_INSET));
    well.setAttribute("y", String(wellY));
    well.setAttribute("width", String(NODE_W - NOTE_INSET * 2));
    well.setAttribute("height", String(Math.max(0, h - wellY - NOTE_BOT_GAP)));
    well.setAttribute("rx", "3");
    well.setAttribute("fill", p.noteWell);
    g.append(well);

    const seam = document.createElementNS(SVGNS, "path");
    seam.setAttribute("d", `M ${NOTE_INSET} ${seamY} L ${NODE_W - NOTE_INSET} ${seamY}`);
    seam.setAttribute("stroke", p.nodeStroke);
    seam.setAttribute("stroke-width", "1");
    g.append(seam);

    if (showText) {
      const tx = NOTE_INSET + NOTE_PAD_X;
      const text = document.createElementNS(SVGNS, "text");
      text.setAttribute("x", String(tx));
      text.setAttribute("y", String(wellY + NOTE_PAD_Y + NOTE_FONT_SIZE));
      text.setAttribute("fill", p.noteInk);
      text.setAttribute("font-family", LABEL_FONT);
      text.setAttribute("font-size", String(NOTE_FONT_SIZE));
      text.style.userSelect = "none";
      lines.forEach((line, i) => {
        const tspan = document.createElementNS(SVGNS, "tspan");
        tspan.setAttribute("x", String(tx));
        if (i > 0) tspan.setAttribute("dy", String(NOTE_LINE_H));
        tspan.textContent = line || " ";
        text.append(tspan);
      });
      g.append(text);
    }
    return g;
  }

  /** Light the inner pin of every port that currently carries a wire. */
  private refreshPortStates(): void {
    const wired = new Set<string>();
    // Channels whose Rec Point tap carries an audible wire. A tapped route leaves
    // the top jack, so it lights that pin instead of the right-edge output — the
    // right edge stays dark unless something actually goes through the mixer stage.
    const tapWired = new Set<string>();
    for (const c of this.plan.connections) {
      // A wire to a hidden endpoint is not drawn, so its ports must not read as
      // in use (e.g. a hidden ducker's key source on the still-visible source).
      if (!this.isWireShown(c)) continue;
      // An off / muted wire recedes, so its jacks must not glow as live either — a
      // port lights only when it carries at least one audible (non-off) connection.
      if (this.isOffSend(c)) continue;
      if (this.isRecPointTap(c.from, c.to)) tapWired.add(parseRef(c.from).nodeId);
      else wired.add(c.from);
      wired.add(c.to);
    }
    for (const [r, pin] of this.portPinEls) this.setPinState(pin, wired.has(r), parseRef(r).nodeId);
    for (const [id, pin] of this.tapPinEls) this.setPinState(pin, tapWired.has(id), id);
  }

  /** Paint one jack pin lit (rail color, glowing) or dark. */
  private setPinState(pin: SVGCircleElement, on: boolean, nodeId: string): void {
    const kind = this.nodeById.get(nodeId)!.kind;
    pin.setAttribute("fill", on ? this.palette.rail[kind] : this.palette.portPinOff);
    pin.setAttribute("r", String(on ? PIN_R_ON : PIN_R_OFF));
    if (on) pin.setAttribute("filter", "url(#jack-glow)");
    else pin.removeAttribute("filter");
  }

  // Where a wire leaves its source and, for a Rec Point tap, the jack it rises from
  // first. The tap climbs a straight riser and only then sweeps across: a single
  // cubic bent upward still dives back through the channel's own faceplate as soon
  // as the destination sits below it, hiding the exit the tap exists to show.
  // Shared by the committed wire and the drag's rubber band so the two exit the
  // same way and the band never jumps on release.
  private wireAnchor(from: string, tap: boolean): { riser: Pt | null; a: Pt } {
    if (!tap) return { riser: null, a: this.portPoint(from) };
    const jack = this.tapPoint(parseRef(from).nodeId);
    return { riser: jack, a: { x: jack.x, y: jack.y - TAP_RISE } };
  }

  /** Path prefix that walks a riser (if any) up to the cubic's start point. */
  private static pathHead(riser: Pt | null, a: Pt): string {
    return riser ? `M ${riser.x} ${riser.y} L ${a.x} ${a.y}` : `M ${a.x} ${a.y}`;
  }

  // The full curve of a committed wire. wirePath and wirePoint both read it from
  // here, so the PRE marker can never drift off the line it annotates.
  private wireGeometry(from: string, to: string): { riser: Pt | null; a: Pt; b: Pt; c1: Pt; c2: Pt } {
    const b = this.portPoint(to);
    const { riser, a } = this.wireAnchor(from, this.isRecPointTap(from, to));
    const dx = Math.max(40, Math.abs(b.x - a.x) * 0.5);
    return { riser, a, b, c1: { x: a.x + dx, y: a.y }, c2: { x: b.x - dx, y: b.y } };
  }

  private wirePath(from: string, to: string): string {
    const { riser, a, b, c1, c2 } = this.wireGeometry(from, to);
    return `${Graph.pathHead(riser, a)} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${b.x} ${b.y}`;
  }

  // Point and tangent angle (deg) at parameter t on the same cubic the wire uses,
  // for placing the PRE tap marker along the curve near the source end.
  private wirePoint(from: string, to: string, t: number): { x: number; y: number; angle: number } {
    const { a, b, c1, c2 } = this.wireGeometry(from, to);
    const mt = 1 - t;
    const at = (p0: number, p1: number, p2: number, p3: number): number =>
      mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
    const slope = (p0: number, p1: number, p2: number, p3: number): number =>
      3 * mt * mt * (p1 - p0) + 6 * mt * t * (p2 - p1) + 3 * t * t * (p3 - p2);
    return {
      x: at(a.x, c1.x, c2.x, b.x),
      y: at(a.y, c1.y, c2.y, b.y),
      angle: (Math.atan2(slope(a.y, c1.y, c2.y, b.y), slope(a.x, c1.x, c2.x, b.x)) * 180) / Math.PI,
    };
  }

  private redrawWires(): void {
    this.retrace();
    this.recue();
    this.wireLayer.replaceChildren();
    // Partition the visible wires into off / on in one pass. Off / -∞ sends paint
    // first (behind) and the live (on) wires last (on top), so in the dense always-
    // wired mesh the on wires — and their click bands — sit in front and are easier
    // to pick. Each list keeps plan order, so the last on wire stays the most recent.
    const off: PlanConnection[] = [];
    const on: PlanConnection[] = [];
    for (const conn of this.plan.connections) {
      // A wire to a shelved endpoint would dangle into empty space; skip any wire
      // whose node is hidden, so a shelved node takes its wires off-canvas with it.
      if (!this.isWireShown(conn)) continue;
      const isOff = this.isOffSend(conn);
      // Declutter toggle: drop the off / -∞ sends entirely (they are non-removable
      // fixed wires, so hiding is the only way to thin the always-wired send mesh).
      if (this.hideOffSends && isOff) continue;
      (isOff ? off : on).push(conn);
    }
    for (const conn of off) this.wireLayer.append(this.makeWire(conn));
    for (const conn of on) this.wireLayer.append(this.makeWire(conn));
    // Jacks share the wires' off-state, so refresh them in lockstep: a wire-only
    // repaint (a send on/off, a node mute) must re-evaluate which ports stay lit.
    this.refreshPortStates();
    // A rewire or a moved node moves the CUE bus's path and the lines drawn for it, and the
    // node fades follow the path.
    if (this.cuePathNodes.size || this.cueLayer.childElementCount) this.highlightSelectedNode();
  }

  private makeWire(conn: PlanConnection): SVGGElement {
    const g = document.createElementNS(SVGNS, "g");
    const d = this.wirePath(conn.from, conn.to);
    const selected =
      this.selection?.type === "conn" && this.selection.from === conn.from && this.selection.to === conn.to;
    const fromId = parseRef(conn.from).nodeId;
    const toId = parseRef(conn.to).nodeId;
    // An off / -∞ send recedes: faint and finely dotted, so a board of always-wired
    // sends reads as "these few are live, the rest are parked at -∞". A lit/selected
    // wire ignores it so the user can still see and edit the one they picked.
    const off = this.isOffSend(conn);
    // When node(s) are selected, light wires incident to any of them and fade the
    // rest so the selection's routing stands out in a dense diagram. Uses the whole
    // multi-selection, not just the anchor, so it matches the node highlighting.
    const hasNodeSel = this.selectedNodes.size > 0;
    // A traced signal path lights its wires the same way: both endpoints in the
    // upstream closure and the wire itself live (an off send between two closure
    // nodes is a parallel silent route, not part of the flow being traced). While a
    // trace is active it alone decides what lights, so a wire merely touching the
    // selected node does not.
    const pathActive = this.pathNodes.size > 0;
    const incident =
      hasNodeSel && !pathActive && !this.pathEmpty && (this.selectedNodes.has(fromId) || this.selectedNodes.has(toId));
    const inPath = pathActive && !off && this.pathNodes.has(fromId) && this.pathNodes.has(toId);
    // While something is cued, the wires the CUE bus is fed through stay as they are and the
    // rest fade like an off-path wire. A wire into a monitor that plays the CUE bus is the feed
    // the unit drops for as long as the cue lasts, so it is drawn faint and dashed.
    const cueActive = this.cuePathNodes.size > 0;
    const cueCarried = cueActive && !off && this.cuePathNodes.has(fromId) && this.cuePathNodes.has(toId);
    const cueReplaced = cueActive && this.cueMonitors.includes(toId);
    const lit = selected || incident || inPath;
    const faded = ((hasNodeSel && !this.pathEmpty) || pathActive || (cueActive && !cueCarried)) && !lit;

    // Invisible wide band along the wire so the thin curve is easy to click; it
    // carries the pointer handler while the painted wire below stays thin. The
    // transparent stroke is inline so the PNG/PDF export stays invisible.
    const hit = document.createElementNS(SVGNS, "path");
    hit.classList.add("wire-hit");
    hit.setAttribute("data-from", conn.from);
    hit.setAttribute("data-to", conn.to);
    hit.setAttribute("d", d);
    hit.setAttribute("fill", "none");
    hit.setAttribute("stroke", "transparent");
    hit.setAttribute("stroke-width", String(WIRE_HIT_W));
    hit.setAttribute("stroke-linecap", "round");
    hit.style.pointerEvents = "stroke";
    hit.style.cursor = "pointer";
    hit.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
      this.select({ type: "conn", from: conn.from, to: conn.to });
    });
    // Explain on hover what the geometry alone cannot say — the stage a Rec Point
    // tap takes, or whether a MAIN / LINE patch can be switched to mono — in the
    // same words the inspector puts on the wire when it is selected. One classifier
    // (sendlessNote) serves both, so hover and selection cannot drift apart, and
    // its union return type refuses a new case until that case has its own wording.
    // Touch has no hover, so selecting still carries the note.
    const note = sendlessNote(this.model, conn.from, conn.to);
    if (note !== "selectionOnly") {
      const title = document.createElementNS(SVGNS, "title");
      title.textContent = t().inspector[note];
      hit.append(title);
    }
    g.append(hit);

    const color = this.palette.wire[WIRE_GROUP[conn.kind]];
    // A pre-fader send is dashed and tagged so it reads at a glance without
    // opening the inspector; POST (the default) stays solid and unmarked, and so does
    // a PRE tap into a FIXED bus, which the unit takes after the fader anyway.
    const isPre = sendIsPreFader(this.model, this.plan, conn);

    // Soft underlay halo marks a lit/selected wire. Done with a wide, low-
    // opacity stroke rather than an SVG blur filter: a perfectly horizontal
    // wire (source and channel default to the same row) has a zero-height
    // bounding box, which collapses an objectBoundingBox filter region and
    // makes the filtered wire vanish.
    if (lit) {
      const halo = document.createElementNS(SVGNS, "path");
      halo.setAttribute("d", d);
      halo.setAttribute("fill", "none");
      halo.setAttribute("stroke", color);
      halo.setAttribute("stroke-width", selected ? "9" : "8");
      halo.setAttribute("stroke-linecap", "round");
      halo.setAttribute("opacity", "0.22");
      halo.style.pointerEvents = "none";
      g.append(halo);
    }

    const path = document.createElementNS(SVGNS, "path");
    path.classList.add("wire-paint");
    path.setAttribute("d", d);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", color);
    path.setAttribute("stroke-width", selected ? "3.5" : incident ? "3" : off && !lit ? "1.4" : "2");
    path.setAttribute("stroke-linecap", "round");
    // Off sends sit well below the normal 0.85 so they recede behind live routing,
    // but a selected/incident one snaps to full so it stays editable.
    path.setAttribute(
      "opacity",
      cueReplaced && !lit ? "0.3" : faded ? (off ? "0.08" : "0.16") : lit ? "1" : off ? "0.3" : "0.85",
    );
    // A fine dotted line marks an off send; PRE (a longer dash) wins when both, since
    // the amber PRE glyph already carries the off-ness for a pre-tapped silent send.
    if (cueReplaced) path.setAttribute("stroke-dasharray", "5 4");
    else if (isPre) path.setAttribute("stroke-dasharray", "7 5");
    else if (off) path.setAttribute("stroke-dasharray", "1.5 4");
    path.style.pointerEvents = "none";
    g.append(path);

    if (isPre) g.append(this.makePreMarker(conn.from, conn.to, faded));

    return g;
  }

  // Amber tap glyph + "PRE" label near the source end of a pre-fader send. Inline
  // attributes keep it in the PNG/PDF export.
  private makePreMarker(from: string, to: string, faded: boolean): SVGGElement {
    const g = document.createElementNS(SVGNS, "g");
    g.style.pointerEvents = "none";
    if (faded) g.setAttribute("opacity", "0.2");
    const p = this.wirePoint(from, to, 0.22);

    const tri = document.createElementNS(SVGNS, "path");
    tri.setAttribute("d", "M -7.5 -7.5 L 7.5 0 L -7.5 7.5 Z");
    tri.setAttribute("transform", `translate(${p.x} ${p.y}) rotate(${p.angle})`);
    tri.setAttribute("fill", this.palette.pre);
    tri.setAttribute("stroke", "rgba(0,0,0,0.22)");
    tri.setAttribute("stroke-width", "0.6");
    g.append(tri);

    const label = document.createElementNS(SVGNS, "text");
    label.setAttribute("x", String(p.x));
    label.setAttribute("y", String(p.y - 12));
    label.setAttribute("text-anchor", "middle");
    label.setAttribute("fill", this.palette.preInk);
    label.setAttribute("font-family", LABEL_FONT);
    label.setAttribute("font-size", "10.5");
    label.setAttribute("font-weight", "700");
    label.setAttribute("letter-spacing", "1");
    label.style.userSelect = "none";
    label.textContent = "PRE";
    g.append(label);
    return g;
  }

  /** Repaint wires only (e.g. after a PRE/POST toggle flips a send's marker). */
  repaintWires(): void {
    this.redrawWires();
  }

  // --- selection -----------------------------------------------------------

  private select(sel: Selection): void {
    this.selection = sel;
    this.selectedNodes.clear();
    this.clearTrace();
    if (sel?.type === "node") {
      this.selectedNodes.add(sel.id);
      this.focusNode = sel.id;
    }
    this.redrawWires();
    this.highlightSelectedNode();
    this.renderSelBar();
    this.cb.onSelect(sel);
  }

  /** Ctrl/Cmd-click: add or drop a node from the multi-selection without dragging
   *  it, so several nodes can be shelved at once. The anchor (shown in the
   *  inspector) follows the most recently touched node. */
  private toggleNodeSelection(id: string): void {
    this.clearTrace();
    if (this.selectedNodes.has(id)) {
      this.selectedNodes.delete(id);
      if (this.selection?.type === "node" && this.selection.id === id) {
        const next = [...this.selectedNodes].pop();
        this.selection = next ? { type: "node", id: next } : null;
      }
    } else {
      this.selectedNodes.add(id);
      this.selection = { type: "node", id };
    }
    this.redrawWires();
    this.highlightSelectedNode();
    this.renderSelBar();
    this.cb.onSelect(this.selection);
  }

  /** Trace and highlight the live signal path feeding a node: the node plus every
   *  upstream input / channel / bus that reaches it through live wiring (off / -∞
   *  sends are not followed, or the always-wired mesh would light the whole board).
   *  The node stays selected; a leaf with no upstream just reports it. */
  private highlightPath(id: string): void {
    const closure = this.upstreamOf(id);
    // A leaf (an input) has only itself: nothing upstream to light.
    const hasPath = closure.size > 1;
    this.clearTrace();
    if (hasPath) {
      this.pathNodes = closure;
      this.pathRoot = id;
    } else this.pathEmpty = true;
    this.redrawWires();
    this.highlightSelectedNode();
    this.cb.onStatus(
      hasPath ? t().status.pathTraced(this.labelOf(id), closure.size) : t().status.pathNone(this.labelOf(id)),
    );
  }

  /** The live signal closure feeding `id`, the node included. */
  private upstreamOf(id: string, ducked = true): Set<string> {
    return upstreamNodes(
      this.plan,
      id,
      (c) => !this.isOffSend(c),
      (cur, via) => this.duckerFeeders(cur, via ? !tapAheadOfDucker(this.model, this.plan, via) : cur !== id || ducked),
    );
  }

  /** A ducker that is on and keyed by a live wire. */
  private isActiveDucker(id: string): boolean {
    const node = this.nodeById.get(id);
    return (
      node?.kind === "ducker" &&
      !this.isNodeInactive(node) &&
      this.plan.connections.some((c) => parseRef(c.to).nodeId === id && !this.isOffSend(c))
    );
  }

  /** What shapes a node's signal with no wire into it: the channel an active ducker ducks,
   *  and — where the channel is taken after its Ducker (`ducked`) — the active duckers hung
   *  under it, whose key sets its level. */
  private duckerFeeders(id: string, ducked: boolean): string[] {
    const parent = this.parentOf(id);
    const own = parent !== undefined && this.isActiveDucker(id) ? [parent] : [];
    return ducked ? [...own, ...this.attachedDescendants(id).filter((d) => this.isActiveDucker(d))] : own;
  }

  private clearTrace(): void {
    this.pathNodes.clear();
    this.pathRoot = null;
    this.pathEmpty = false;
  }

  /** Take the trace again from its root against the plan as it stands. A root no longer on
   *  the board, or one nothing feeds any more, ends the trace. */
  private retrace(): void {
    const root = this.pathRoot;
    if (root === null) return;
    const closure = this.isHidden(root) ? null : this.upstreamOf(root);
    if (closure && closure.size > 1) this.pathNodes = closure;
    else this.clearTrace();
  }

  /** Show what is cued (the unit's [CUE] flags, outside the plan). Empty shows nothing. */
  setCues(nodes: readonly string[]): void {
    this.cued = [...nodes];
    this.redrawWires();
    this.highlightSelectedNode();
  }

  /**
   * Take the CUE bus's path again against the plan as it stands: each drawn cued channel with
   * everything that feeds it, each drawn cued output (STEREO, a MIX bus, STREAMING) alone, and
   * the drawn monitors whose Cue Interrupt (on unless the plan turns it off) makes them play the
   * CUE bus in place of their own source. Nothing while nothing drawn is cued, or while an export
   * is being taken.
   */
  private recue(): void {
    this.cuePathNodes = new Set();
    this.cueMonitors = [];
    if (this.cueHeld) return;
    for (const id of this.cued) {
      if (!this.nodeById.has(id) || this.isHidden(id)) continue;
      if (cueAddrOf(this.model, id)?.group === "output") this.cuePathNodes.add(id);
      // The CUE bus takes a channel ahead of its ducker, so no ducker joins its path.
      else for (const n of this.upstreamOf(id, false)) this.cuePathNodes.add(n);
    }
    if (!this.cuePathNodes.size) return;
    this.cueMonitors = ["bus.mon1", "bus.mon2"].filter(
      (id) => this.nodeById.has(id) && !this.isHidden(id) && (this.plan.nodeParams[id]?.cueInterrupt ?? true),
    );
    for (const id of this.cueMonitors) this.cuePathNodes.add(id);
  }

  /** The frame drawn around a node on the CUE bus's path, outside its jacks. */
  private cueFrame(id: string): { x: number; y: number; w: number; h: number } {
    const p = this.posOf(id);
    return {
      x: p.x - CUE_FRAME_PAD,
      y: p.y - CUE_FRAME_PAD,
      w: NODE_W + 2 * CUE_FRAME_PAD,
      h: this.nodeHeight(id) + 2 * CUE_FRAME_PAD,
    };
  }

  /**
   * Draw what is cued over the board: a frame around every node on the CUE bus's path, a CUE
   * badge on each monitor that plays it, and a line from each cued strip to each of those
   * monitors. A line runs between the two frames' outer edges and never into a frame.
   */
  private drawCues(): void {
    this.cueLayer.replaceChildren();
    if (!this.cuePathNodes.size) return;
    const p = this.palette;
    const edge = CUE_FRAME_W / 2;
    for (const id of this.cuePathNodes) {
      const f = this.cueFrame(id);
      const rect = document.createElementNS(SVGNS, "rect");
      rect.setAttribute("x", String(f.x));
      rect.setAttribute("y", String(f.y));
      rect.setAttribute("width", String(f.w));
      rect.setAttribute("height", String(f.h));
      rect.setAttribute("rx", "8");
      rect.setAttribute("fill", "none");
      rect.setAttribute("stroke", p.cue);
      rect.setAttribute("stroke-width", String(CUE_FRAME_W));
      rect.dataset.cueFrame = id;
      this.cueLayer.append(rect);
    }
    for (const mon of this.cueMonitors) {
      const f = this.cueFrame(mon);
      const badge = document.createElementNS(SVGNS, "g");
      badge.dataset.cueBadge = mon;
      // Astride the frame's top edge at its right end.
      const w = 34;
      const h = 14;
      const bx = f.x + f.w - w + edge;
      const by = f.y - h / 2;
      const face = document.createElementNS(SVGNS, "rect");
      face.setAttribute("x", String(bx));
      face.setAttribute("y", String(by));
      face.setAttribute("width", String(w));
      face.setAttribute("height", String(h));
      face.setAttribute("rx", "4");
      face.setAttribute("fill", p.cue);
      const label = document.createElementNS(SVGNS, "text");
      label.setAttribute("x", String(bx + w / 2));
      label.setAttribute("y", String(by + h / 2));
      label.setAttribute("text-anchor", "middle");
      label.setAttribute("dominant-baseline", "central");
      label.setAttribute("fill", p.cueInk);
      label.setAttribute("font-family", LABEL_FONT);
      label.setAttribute("font-size", "9.5");
      label.setAttribute("font-weight", "700");
      label.setAttribute("letter-spacing", "1");
      label.textContent = "CUE";
      badge.append(face, label);
      this.cueLayer.append(badge);
    }
    for (const id of this.cued) {
      if (!this.cuePathNodes.has(id)) continue;
      const from = this.cueFrame(id);
      const a = { x: from.x + from.w + edge, y: this.posOf(id).y + NODE_H / 2 };
      for (const mon of this.cueMonitors) {
        const to = this.cueFrame(mon);
        const b = { x: to.x - edge, y: this.posOf(mon).y + NODE_H / 2 };
        const dx = Math.max(40, Math.abs(b.x - a.x) * 0.5);
        const line = document.createElementNS(SVGNS, "path");
        line.setAttribute("d", `M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`);
        line.setAttribute("fill", "none");
        line.setAttribute("stroke", p.cue);
        line.setAttribute("stroke-width", "2.5");
        line.dataset.cueLine = `${id} ${mon}`;
        this.cueLayer.append(line);
      }
    }
  }

  /** Clear any selection (used by the canvas, the action bar, and Escape). */
  clearSelection(): void {
    if (!this.selection && !this.selectedNodes.size) return;
    this.select(null);
  }

  private highlightSelectedNode(): void {
    this.retrace();
    const anchor = this.selection?.type === "node" ? this.selection.id : null;
    // While a path trace is active, fade the off-path nodes so the lit chain stands
    // out in the node layer too — the same lit / faded split the wires already use.
    const pathActive = this.pathNodes.size > 0;
    for (const [id, el] of this.nodeEls) {
      const rect = el.querySelector("rect")!;
      const node = this.nodeById.get(id)!;
      const on = this.selectedNodes.has(id);
      // A traced-path node (not itself selected) wears the accent frame too, so the
      // upstream chain reads as one highlighted group with the double-clicked node.
      const onPath = !on && this.pathNodes.has(id);
      // Fade off-path nodes to a fraction of their resting opacity (so a muted /
      // unread node keeps its own dim), and restore the rest to that base.
      const base = this.restingOpacity(node);
      const fadeOff =
        (pathActive && !this.pathNodes.has(id)) || (this.cuePathNodes.size > 0 && !this.cuePathNodes.has(id));
      // A badged node carries its dim on its body, so the badge beside it keeps full
      // strength; the node itself then carries the path fade alone.
      const body = el.querySelector(":scope > .node-body");
      if (body) {
        body.setAttribute("opacity", String(base));
        el.setAttribute("opacity", fadeOff ? "0.3" : "1");
      } else el.setAttribute("opacity", String(fadeOff ? +(base * 0.3).toFixed(3) : base));
      const disabled = !on && !onPath && this.disabledNodes.has(id);
      // Unread frame ranks below selected/path/disabled but above the plain frame, so
      // a re-highlight restores it instead of reverting an unread node to normal.
      const unread = !on && !onPath && !disabled && this.unreadNodes.has(id) && !this.isNodeInactive(node);
      el.classList.toggle("selected", on);
      el.setAttribute("aria-pressed", String(on));
      rect.setAttribute("stroke-width", on ? "2.5" : onPath ? "2" : disabled ? "1.5" : unread ? "1.2" : "1");
      rect.setAttribute(
        "stroke",
        on || onPath ? this.palette.tempWire : disabled || unread ? this.palette.warn : this.palette.nodeStroke,
      );
      if (disabled) rect.setAttribute("stroke-dasharray", "4 3");
      else if (unread) rect.setAttribute("stroke-dasharray", "2 3");
      else rect.removeAttribute("stroke-dasharray");
    }
    this.syncRovingNode();
    this.drawCues();
    // Raise the anchor node so its note panel sits above any neighbor below it. Moving the
    // element takes keyboard focus off it, so a focused anchor is focused again.
    if (anchor) {
      const el = this.nodeEls.get(anchor);
      if (el) {
        const focused = this.focusedNodeId() === anchor;
        this.nodeLayer.append(el);
        if (focused) el.focus({ preventScroll: true });
      }
    }
  }

  deleteSelection(): void {
    if (this.selection?.type === "conn") {
      this.deleteConnection(this.selection.from, this.selection.to);
    }
  }

  deleteConnection(from: string, to: string): void {
    if (!this.cb.mayEdit()) return;
    if (isFixedConnection(this.model, from, to)) {
      this.cb.onStatus(t().status.fixedConnection);
      return;
    }
    // A receiver the unit never leaves without a source keeps its last wire; another source
    // drawn onto it replaces that wire instead (finishConnect).
    if (isLastRequiredSource(this.model, this.plan, from, to)) {
      this.cb.onStatus(t().status.streamingSourceRequired);
      return;
    }
    const kind = ruleKind(this.model, from, to);
    this.plan.connections = this.plan.connections.filter((c) => !(c.from === from && c.to === to));
    // Removing a paired channel's source also clears its partner's mirrored one.
    if (kind === "source") {
      const partner = partnerChannel(this.model, parseRef(to).nodeId);
      if (partner) {
        const mirrorTo = ref(partner, parseRef(to).portId);
        this.plan.connections = this.plan.connections.filter((c) => !(c.to === mirrorTo && isSingleInput(c.kind)));
      }
    }
    const isSel = this.selection?.type === "conn" && this.selection.from === from && this.selection.to === to;
    // Both paths redraw the wires (select via its own redraw), which ends with
    // refreshPortStates, so the port glow is restored without a second call.
    if (isSel) this.select(null);
    else this.redrawWires();
    this.cb.onChange();
    this.cb.onStatus(t().status.connectionDeleted);
  }

  // --- interaction ---------------------------------------------------------

  private onPointerDown(e: PointerEvent): void {
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // A second finger turns any in-flight drag/connect/pan into a pinch.
    if (this.pointers.size === 2) {
      e.preventDefault();
      this.closeNoteEditor();
      this.cancelInteraction();
      this.lastNodeClick = null;
      this.beginPinch();
      this.capturePointer(e.pointerId);
      return;
    }

    this.closeNoteEditor();
    const target = e.target as Element;

    // Header note controls live inside the node group; read the id from it.
    const btnNode =
      target.closest(".note-toggle") || target.closest(".note-add")
        ? (target.closest(".node") as SVGGElement | null)
        : null;
    if (btnNode) {
      e.preventDefault();
      const id = btnNode.dataset.id!;
      if (target.closest(".note-toggle")) {
        this.select({ type: "node", id });
        this.toggleNoteCollapse(id);
      } else {
        this.openNoteEditor(id);
      }
      return;
    }

    // The Rec Point tap's target is a rect, the side ports' are circles, so this
    // is typed at the element they share.
    const portEl = target.closest(".port") as SVGElement | null;
    if (portEl) {
      e.preventDefault();
      const dir: PortDirection = portEl.classList.contains("port-out") ? "out" : "in";
      // A Rec Point tap carries the channel's own `ch:out` ref in data-tap, so the
      // plan is unaffected by which of the channel's two jacks was pressed — only
      // the routes offered and the wire's exit point are.
      const tap = portEl.classList.contains("port-tap");
      const ref = tap ? portEl.dataset.tap! : portEl.dataset.ref!;
      this.connect = { ref, dir, tap, startX: e.clientX, startY: e.clientY, mode: "pending" };
      this.capturePointer(e.pointerId);
      return;
    }

    const nodeEl = target.closest(".node") as SVGGElement | null;
    if (nodeEl) {
      e.preventDefault();
      const id = nodeEl.dataset.id!;
      // Ctrl/Cmd-click toggles the node in a multi-selection instead of dragging
      // or editing it; the action bar then offers to shelve them together.
      if (e.metaKey || e.ctrlKey) {
        this.lastNodeClick = null;
        this.toggleNodeSelection(id);
        return;
      }
      const pos = this.posOf(id);
      const p = this.clientToContent(e);
      // On an already-selected node, pressing in its open note area edits the
      // note; the header (outside the note area) still drags. Other nodes drag
      // as usual — select first, then click the note to edit.
      const inNoteArea = p.y - pos.y > NODE_H;
      const isSelected = this.selection?.type === "node" && this.selection.id === id;
      const noteShown = this.noteLines(id).length > 0 && !this.collapsed.has(id);
      if (isSelected && noteShown && inNoteArea) {
        this.openNoteEditor(id);
        return;
      }
      // A second press on the same node within the threshold opens its note editor;
      // a single sustained press (handled below by the long-press timer) traces the
      // signal path instead, so the two gestures don't collide.
      const now = performance.now();
      if (this.lastNodeClick?.id === id && now - this.lastNodeClick.time < 350) {
        this.lastNodeClick = null;
        this.openNoteEditor(id);
        return;
      }
      this.lastNodeClick = { id, time: now };
      // Grabbing a hung node (ducker) drags its parent so the unit moves as one;
      // selection still lands on the pressed node.
      const dragId = this.parentOf(id) ?? id;
      const dragPos = this.posOf(dragId);
      // A STEREO-linked MONO IN pair moves as one: capture the visible partner's
      // offset so it tracks the dragged node (like a ducker, but with its own
      // position). Skipped when the partner is hidden (no tie to keep in step).
      const pair = this.linkedPairOf(dragId);
      const partner = pair ? (pair[0] === dragId ? pair[1] : pair[0]) : null;
      const link =
        pair && partner && !this.isHidden(partner)
          ? { partner, dx: this.posOf(partner).x - dragPos.x, dy: this.posOf(partner).y - dragPos.y, pair }
          : null;
      this.dragNode = { id: dragId, grabDx: p.x - dragPos.x, grabDy: p.y - dragPos.y, link, moved: false };
      this.select({ type: "node", id });
      this.capturePointer(e.pointerId);
      // Holding the press still (no drag) traces the pressed node's signal path.
      this.startLongPress(id, e.clientX, e.clientY);
      return;
    }

    // empty canvas: start panning + clear selection
    this.select(null);
    this.autoFit = false;
    this.panning = { startX: e.clientX, startY: e.clientY, panX: this.pan.x, panY: this.pan.y };
    this.capturePointer(e.pointerId);
  }

  private capturePointer(id: number): void {
    try {
      this.svg.setPointerCapture(id);
    } catch {
      /* synthetic / inactive pointer */
    }
  }

  // Arm the path-trace timer for a node press. If it fires (the pointer stayed put
  // for LONG_PRESS_MS) it cancels the pending drag and traces the node's path.
  private startLongPress(id: string, x: number, y: number): void {
    this.cancelLongPress();
    const timer = setTimeout(() => {
      this.longPress = null;
      // The hold won: drop the pending drag so a later move can't reposition the
      // node, then trace its signal path.
      this.endNodeDrag();
      this.highlightPath(id);
    }, LONG_PRESS_MS);
    this.longPress = { id, timer, x, y };
  }

  private cancelLongPress(): void {
    if (!this.longPress) return;
    clearTimeout(this.longPress.timer);
    this.longPress = null;
  }

  private onPointerMove(e: PointerEvent): void {
    // A mouse moving with no button held has released whatever it pressed, whether or not
    // the release reached the page — the native context menu takes the right button's —
    // so the press it started ends here rather than following the cursor.
    if (mouseMovedUnpressed(e)) {
      if (this.pointers.has(e.pointerId)) this.endLostPress(e.pointerId);
      return;
    }
    if (this.pointers.has(e.pointerId)) this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pinch) {
      this.updatePinch();
      return;
    }
    // While a long-press is pending, swallow small jitter so the node stays put and
    // the hold can complete; once the pointer travels past the tolerance the press
    // is a drag, so cancel the trace timer and fall through to move the node.
    if (this.longPress) {
      if (Math.hypot(e.clientX - this.longPress.x, e.clientY - this.longPress.y) < LONG_PRESS_TOLERANCE) return;
      this.cancelLongPress();
    }
    if (this.dragNode) {
      // The press became a drag, so it is no longer half of a double-press. The
      // detector times pointerdown-to-pointerdown and was not invalidated here, so a
      // flick-drag released at ~250 ms and grabbed again at ~300 ms to keep positioning
      // opened the note editor instead — and on a collapsed note that also un-collapsed
      // it and marked the plan changed.
      if (!this.dragNode.moved) this.lastNodeClick = null;
      // Asked before every move writes the place, so a refused drag ends here having moved
      // nothing — and, being a drag, is not half of a double-press either.
      if (!this.cb.mayEdit()) {
        this.endNodeDrag();
        return;
      }
      this.dragNode.moved = true;
      const p = this.clientToContent(e);
      this.plan.positions[this.dragNode.id] = {
        x: p.x - this.dragNode.grabDx,
        y: p.y - this.dragNode.grabDy,
      };
      const pos = this.plan.positions[this.dragNode.id];
      // Guard the element like the descendant/partner moves below: a re-render
      // racing pointermove can drop it momentarily, and an unguarded ! would
      // crash the drag with an uncaught TypeError.
      this.nodeEls.get(this.dragNode.id)?.setAttribute("transform", `translate(${pos.x} ${pos.y})`);
      // Hung descendants follow: their positions derive from the parent (a single
      // ducker, or the SD Rec track-slot chain), so move each element. Their wires
      // are redrawn once with the rest, after every move below.
      for (const descId of this.attachedDescendants(this.dragNode.id)) {
        if (this.isHidden(descId)) continue;
        const cpos = this.posOf(descId);
        this.nodeEls.get(descId)?.setAttribute("transform", `translate(${cpos.x} ${cpos.y})`);
      }
      // A STEREO-linked partner moves by the captured offset (its own position),
      // and the heart tie is redrawn to track the pair.
      const link = this.dragNode.link;
      if (link) {
        const fp = { x: pos.x + link.dx, y: pos.y + link.dy };
        this.plan.positions[link.partner] = fp;
        this.nodeEls.get(link.partner)?.setAttribute("transform", `translate(${fp.x} ${fp.y})`);
        this.redrawStereoLink(link.pair[0], link.pair[1]);
      }
      // Every moved element's position is written above, so one redraw repaints all
      // affected wires; per-node redraws would rebuild identical DOM discarded pre-paint.
      this.redrawWires();
      return;
    }
    if (this.connect) {
      if (this.connect.mode === "pending") {
        const moved = Math.hypot(e.clientX - this.connect.startX, e.clientY - this.connect.startY);
        if (moved < DRAG_THRESHOLD) return;
        this.connect.mode =
          this.cb.mayEdit() && this.beginConnect(this.connect.ref, this.connect.dir, this.connect.tap)
            ? "connecting"
            : "noop";
      }
      if (this.connect.mode === "connecting") {
        const p = this.clientToContent(e);
        this.updateTempWire(this.connect.ref, this.connect.dir, this.connect.tap, p);
      }
      return;
    }
    if (this.panning) {
      this.pan.x = this.panning.panX + (e.clientX - this.panning.startX);
      this.pan.y = this.panning.panY + (e.clientY - this.panning.startY);
      this.applyTransform();
    }
  }

  private onPointerUp(e: PointerEvent): void {
    this.pointers.delete(e.pointerId);
    // A release before the timer fires means it was a click / drag, not a hold.
    this.cancelLongPress();
    // Lifting one finger ends the pinch; the other finger is left idle (no pan
    // resumes) until it too lifts, avoiding a jump back to single-pointer drag.
    if (this.pinch && this.pointers.size < 2) {
      this.pinch = null;
      try {
        this.svg.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer was not captured */
      }
      return;
    }
    this.endNodeDrag();
    if (this.connect) {
      const c = this.connect;
      this.connect = null;
      if (c.mode === "connecting") {
        // Pointer capture retargets pointerup to the svg, so hit-test the port
        // under the release point rather than trusting e.target.
        const wantClass = c.dir === "out" ? ".port-in" : ".port-out";
        const under = document.elementFromPoint(e.clientX, e.clientY);
        const target = under?.closest(wantClass) as SVGElement | null;
        // Whichever end of the drag is the source side decides whether the route
        // was taken from the Rec Point tap: the origin when dragging out of it, the
        // released jack when dragging back from an input.
        const releasedTap = target?.classList.contains("port-tap") ?? false;
        const ref = target ? (target.dataset.ref ?? target.dataset.tap ?? null) : null;
        this.finishConnect(c.ref, c.dir, ref, c.dir === "out" ? c.tap : releasedTap);
      } else if (c.dir === "in") {
        // A click (or a drag with no legal source) on an input port selects its
        // incoming wire (selectInputWire), mirroring a click on the wire itself.
        this.selectInputWire(c.ref);
      }
    }
    this.panning = null;
    try {
      this.svg.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer was not captured */
    }
  }

  // End the in-flight node drag, reporting a moved one exactly once. Every path that
  // drops a drag comes through here: a release, a pointer-capture revocation, and the
  // second finger that turns the drag into a pinch. pointermove has already written
  // the new place into the plan (the dragged node, plus a STEREO-linked partner's own
  // position), so a teardown that skipped the report would leave the plan holding a
  // position with no dirty flag, no live-sync schedule and no undo entry — a move the
  // operator cannot take back.
  private endNodeDrag(): void {
    if (!this.dragNode) return;
    const moved = this.dragNode.moved;
    this.dragNode = null;
    // A plain select-click leaves the node where it was; only a real drag
    // persists a new position, so only that marks the plan dirty.
    if (moved) this.cb.onChange();
  }

  // Pointer capture can be revoked without a pointerup (touch-scroll gesture,
  // context menu, alert). Tear down any in-flight interaction so the rubber-band
  // wire and port highlights don't linger and dragNode/panning stop firing.
  private cancelInteraction(): void {
    if (this.connect?.mode === "connecting") {
      this.tempWire?.remove();
      this.tempWire = null;
      this.clearPortHighlights();
    }
    this.connect = null;
    this.panning = null;
    this.cancelLongPress();
    // Last, so the change funnel (and the inspector rebuild behind it) runs with every
    // other interaction already torn down. In the pinch branch this also puts the
    // rebuild before beginPinch's own measurement rather than after it.
    this.endNodeDrag();
  }

  /** End the press of a pointer whose release never arrived: a pan stops where it is, a
   *  connect drag draws nothing, and a moved node is reported once. */
  private endLostPress(pointerId: number): void {
    this.pointers.delete(pointerId);
    try {
      this.svg.releasePointerCapture(pointerId);
    } catch {
      /* pointer was not captured */
    }
    this.cancelInteraction();
  }

  private onPointerCancel(e: PointerEvent): void {
    this.pointers.delete(e.pointerId);
    if (this.pinch && this.pointers.size < 2) this.pinch = null;
    this.cancelInteraction();
  }

  /**
   * Drop every pointer this view is tracking — what a window blur ends, where the two
   * pointer ends above each speak for one pointer and are told which. The host calls it too,
   * before the board stops taking edits, so a moved node is reported while it still can be.
   *
   * `cancelInteraction` alone is not that: it clears the interaction but leaves
   * `pointers`, `pinch` and the captures, all three of which the pointer ends do clear.
   * A pinch survives that, and `onPointerMove` tests `pinch` before everything the
   * interaction teardown touched — so two fingers went on zooming while another
   * application was frontmost. The stale entries are the second half: a touch pointer id
   * is fresh per press, so one left behind makes the next single press read as a second
   * finger and the view opens a pinch nobody started.
   */
  endAllPointers(): void {
    for (const id of this.pointers.keys()) {
      try {
        this.svg.releasePointerCapture(id);
      } catch {
        /* pointer was not captured */
      }
    }
    this.pointers.clear();
    this.pinch = null;
    this.cancelInteraction();
  }

  // Snapshot the two-finger distance and midpoint that updatePinch advances from,
  // caching the svg origin so the move loop never re-measures it.
  private beginPinch(): void {
    this.autoFit = false;
    const rect = this.svg.getBoundingClientRect();
    const m = this.fingerSpread(rect.left, rect.top);
    if (m) this.pinch = { ...m, left: rect.left, top: rect.top };
  }

  // Current finger spread and midpoint, the midpoint in svg-local coordinates
  // (left/top are the cached svg origin, not re-measured per frame).
  private fingerSpread(left: number, top: number): { lastDist: number; lastCx: number; lastCy: number } | null {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return null;
    const [a, b] = pts;
    return {
      lastDist: Math.hypot(a.x - b.x, a.y - b.y),
      lastCx: (a.x + b.x) / 2 - left,
      lastCy: (a.y + b.y) / 2 - top,
    };
  }

  // Zoom by the change in finger spread (anchored at the midpoint) and pan by the
  // midpoint's own movement, so two fingers zoom and drag the canvas together.
  private updatePinch(): void {
    if (!this.pinch) return;
    const m = this.fingerSpread(this.pinch.left, this.pinch.top);
    if (!m || this.pinch.lastDist === 0) return;
    this.zoomAt(m.lastCx, m.lastCy, this.zoom * (m.lastDist / this.pinch.lastDist));
    this.pan.x += m.lastCx - this.pinch.lastCx;
    this.pan.y += m.lastCy - this.pinch.lastCy;
    this.pinch.lastDist = m.lastDist;
    this.pinch.lastCx = m.lastCx;
    this.pinch.lastCy = m.lastCy;
    this.applyTransform();
  }

  // Zoom to `next` (clamped) while keeping the point at svg-local (cx, cy) fixed.
  private zoomAt(cx: number, cy: number, next: number): void {
    const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    this.pan.x = cx - ((cx - this.pan.x) / this.zoom) * z;
    this.pan.y = cy - ((cy - this.pan.y) / this.zoom) * z;
    this.zoom = z;
  }

  /** A wheel gesture keeps the axis of its first event until it ends: a horizontal one (a
   *  trackpad swipe sideways, a tilt wheel, Shift + wheel) pans the board by deltaX, a vertical
   *  one zooms about the pointer, and a ctrl+wheel event — what a trackpad pinch arrives as —
   *  zooms whichever axis the gesture holds. An event that does not move along the axis it is
   *  read on does nothing. */
  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const now = performance.now();
    const g = this.wheelGesture;
    if (g && now - g.last <= WHEEL_GESTURE_GAP_MS) g.last = now;
    else this.wheelGesture = { axis: Math.abs(e.deltaX) > Math.abs(e.deltaY) ? "x" : "y", last: now };
    if (!e.ctrlKey && this.wheelGesture!.axis === "x") {
      if (e.deltaX === 0) return;
      this.autoFit = false;
      this.pan.x -= e.deltaX;
      this.applyTransform();
      return;
    }
    if (e.deltaY === 0) return;
    this.autoFit = false;
    const rect = this.svg.getBoundingClientRect();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    this.zoomAt(e.clientX - rect.left, e.clientY - rect.top, this.zoom * factor);
    this.applyTransform();
  }

  // --- connecting ----------------------------------------------------------

  /** Every jack of a drawn node a wire can be drawn from, each with the drawn ports a drag
   *  from it would be taken on: the drag's own candidates (`connectCandidates`), so an
   *  occupied input the drop replaces the wire on is among them. */
  connectOrigins(nodeId: string): ConnectOrigin[] {
    const node = this.nodeById.get(nodeId);
    if (!node || node.header || this.isHidden(nodeId)) return [];
    const origins: ConnectOrigin[] = [];
    for (const port of node.ports) {
      const r = ref(nodeId, port.id);
      for (const tap of port.direction === "out" && this.hasTapJack(node) ? [false, true] : [false]) {
        const { legal } = this.connectCandidates(r, port.direction, tap);
        const targets = [...legal].filter((t) => !this.isHidden(parseRef(t).nodeId));
        origins.push({ ref: r, dir: port.direction, tap, targets });
      }
    }
    return origins;
  }

  /** Draw a wire from `origin` to `other` through the drag's own commit, as a drag from that
   *  jack released on that port does — refusals, a replaced source and a completed linked
   *  pair included. */
  connectTo(origin: ConnectOrigin, other: string): void {
    if (!this.cb.mayEdit()) return;
    const tapUsed = origin.dir === "out" ? origin.tap : this.isRecPointTap(other, origin.ref);
    this.finishConnect(origin.ref, origin.dir, other, tapUsed);
  }

  /** Whether the board draws this wire: both its nodes are on the board, and the declutter
   *  toggle is not hiding it as an off send. */
  wireDrawn(from: string, to: string): boolean {
    const conn = this.plan.connections.find((c) => c.from === from && c.to === to);
    return !!conn && this.isWireShown(conn) && !(this.hideOffSends && this.isOffSend(conn));
  }

  /** Select a wire the board draws, as a press on it does; one it does not draw stays
   *  unselected. */
  selectConnection(from: string, to: string): void {
    if (this.wireDrawn(from, to)) this.select({ type: "conn", from, to });
  }

  // Enter connect mode from `ref`, highlighting the legal ports on the opposite
  // side and starting a rubber-band wire. Returns false (doing nothing) when the
  // press should fall back to a click: an output with no possible route at all,
  // or an input with no legal source — the latter keeps a press on a full input
  // selecting its incoming wire instead of opening a dead rubber-band.
  private beginConnect(from: string, dir: PortDirection, tap: boolean): boolean {
    const { legal, possible } = this.connectCandidates(from, dir, tap);
    // Gate: outputs open on any possible route (occupied targets still highlight);
    // inputs open only on a legal source, so a full input falls back to wire-select — unless a
    // drop there replaces its wire, which connectCandidates lights as legal.
    if (dir === "out" ? !possible.size : !legal.size) return false;
    this.paintCandidates(from, dir, legal, possible);
    this.tempWire = document.createElementNS(SVGNS, "path");
    this.tempWire.classList.add("overlay-temp");
    this.tempWire.style.pointerEvents = "none";
    this.tempWire.setAttribute("fill", "none");
    this.tempWire.setAttribute("stroke", this.palette.tempWire);
    this.tempWire.setAttribute("stroke-width", "2");
    this.tempWire.setAttribute("stroke-dasharray", "5 4");
    this.overlay.append(this.tempWire);
    return true;
  }

  /** The ports a drag from `from` can end on: `legal` the drops the board takes, `possible`
   *  every rule-defined partner, occupied ones included, shown outline-only so the user can
   *  see where a port could route even when the destination is taken. Shared by the drag's
   *  start and by the repaint a render mid-drag needs, so the two light the same set. */
  private connectCandidates(
    from: string,
    dir: PortDirection,
    tap: boolean,
  ): { legal: Set<string>; possible: Set<string> } {
    let legal = dir === "out" ? legalTargets(this.model, this.plan, from) : legalSources(this.model, this.plan, from);
    let possible = dir === "out" ? possibleTargets(this.model, from) : possibleSources(this.model, from);
    // Two drops the rule engine refuses are taken by finishConnect, so they are lit as legal:
    // a channel of a STEREO-linked pair onto the USB output holding it alone (it brings its
    // partner), and another source onto an input that takes one and holds one (it replaces
    // the wire there).
    for (const r of possible) {
      const [out, into] = dir === "out" ? [from, r] : [r, from];
      if (this.completesLinkedPair(out, into) || this.replacesWire(out, into)) legal.add(r);
    }
    // A channel's two source jacks are separate origins: the Rec Point tap offers
    // only the direct outs and recordings, the right-edge output only the routes
    // through the mixer stage. So an output drag keeps just the side it left from.
    // Only outputs need this — a tap is always an output, so an input drag has
    // nothing to split.
    if (dir === "out") {
      const fromThisJack = (rs: Set<string>): Set<string> =>
        new Set([...rs].filter((r) => this.isRecPointTap(from, r) === tap));
      legal = fromThisJack(legal);
      possible = fromThisJack(possible);
    }
    return { legal, possible };
  }

  /** Reset every port to its default look, then light the partners: legal ones filled,
   *  occupied-but-possible ones outline-only. A legal partner whose drop would replace the
   *  wire already there is ringed in the warn colour instead of the legal one. Dragging back from an input lights each
   *  source at the jack that route would actually leave from, so a channel offers its
   *  tap for a USB / microSD target and its output otherwise.
   *
   *  Its own method because the highlight state lives ONLY in these elements, and a
   *  render or a fine-grained repaint replaces them: a device-follow reflect landing mid-drag left
   *  the operator finishing the connection with no legality cues at all — the drag
   *  itself survives (the state is ref-based and elementFromPoint resolves the fresh
   *  hit discs), so nothing failed, it just went dark. On a dense board those cues are
   *  the whole affordance. `repaintCandidates` re-runs it after a render and after
   *  `repaintDirtyNodes`. */
  private paintCandidates(from: string, dir: PortDirection, legal: Set<string>, possible: Set<string>): void {
    this.clearPortHighlights();
    for (const r of possible) {
      const el = dir === "out" ? this.portEls.get(r) : this.sourceJack(r, from);
      if (!el) continue;
      el.setAttribute("r", String(JACK_R_CANDIDATE));
      if (legal.has(r)) {
        const [out, into] = dir === "out" ? [from, r] : [r, from];
        el.setAttribute("fill", this.palette.legalFill);
        el.setAttribute("stroke", this.replacesWire(out, into) ? this.palette.warn : this.palette.legalStroke);
      } else {
        el.setAttribute("stroke", this.palette.possibleStroke);
      }
    }
  }

  private updateTempWire(from: string, dir: PortDirection, tap: boolean, to: Pt): void {
    if (!this.tempWire) return;
    // Same exit as the committed wire (wireAnchor), but bowed toward a free pointer
    // rather than a target port: outputs leave to the right, inputs to the left.
    const { riser, a } = this.wireAnchor(from, tap);
    const dx = Math.max(40, Math.abs(to.x - a.x) * 0.5);
    const aCtrl = dir === "out" ? a.x + dx : a.x - dx;
    const bCtrl = dir === "out" ? to.x - dx : to.x + dx;
    const d = `${Graph.pathHead(riser, a)} C ${aCtrl} ${a.y}, ${bCtrl} ${to.y}, ${to.x} ${to.y}`;
    this.tempWire.setAttribute("d", d);
  }

  // Commit a wire from a drag that started at `from` (output or input) released
  // over `released`. Normalizes to from=output / to=input so the constraint
  // engine and source mirroring stay direction-agnostic.
  private finishConnect(from: string, dir: PortDirection, released: string | null, tapUsed: boolean): void {
    this.tempWire?.remove();
    this.tempWire = null;
    this.clearPortHighlights();

    if (!released) return;
    const out = dir === "out" ? from : released;
    const into = dir === "out" ? released : from;
    // The Rec Point tap and the mixer-stage output are separate jacks on the same
    // channel, so a route dropped on the wrong one is refused with the jack to use
    // instead. Both would pass canConnect — the rule engine keys off the plan ref,
    // which is `ch:out` either way. Only a route that exists at all earns this
    // message: pointing at the other jack for a target neither can reach would send
    // the user to a second, different failure.
    const kind = ruleKind(this.model, out, into);
    if (kind && this.isRecPointTap(out, into) !== tapUsed) {
      this.cb.onStatus(tapUsed ? t().error.recPointTargets : t().error.recPointRequired);
      return;
    }
    const drop = dropWire(this.model, this.plan, out, into, (id) => !this.isHidden(id));
    if (!drop.ok) {
      this.cb.onStatus(drop.reason ? t().error[drop.reason] : t().error.cannotConnect);
      return;
    }
    // The wire a replacing drop takes the place of goes in the same change, so one undo puts
    // it back. A selection on it goes with it, as a deleted wire's does.
    const dropsSelected = drop.replaces && this.selection?.type === "conn" && this.selection.to === into;
    this.plan.connections = drop.connections;
    // redrawWires ends with refreshPortStates, so the port glow is restored there (select
    // redraws them too).
    if (dropsSelected) this.select(null);
    else this.redrawWires();
    this.cb.onChange();
    this.cb.onStatus(t().status.connected);
  }

  /** A drop of `out` onto `into` that the output already holds, which completes a linked
   *  pair there: the one refused wire the board still takes, adding the partner instead. */
  private completesLinkedPair(out: string, into: string): boolean {
    return completesLinkedPatch(this.model, this.plan, out, into, (id) => !this.isHidden(id));
  }

  /** A drop of `out` onto an input that takes one source and holds another: the board takes
   *  it in place of the wire there rather than refusing it. On a USB output that is anything
   *  but the held channel's mono-pair partner, which joins the output as a second wire. */
  private replacesWire(out: string, into: string): boolean {
    const reason = canConnect(this.model, this.plan, out, into).reason;
    return reason === "singleInput" || reason === "monoPairOnly";
  }

  /** Whether a wire is on the board: neither of its nodes is on the shelf. */
  private isWireShown(c: PlanConnection): boolean {
    return !this.isHidden(parseRef(c.from).nodeId) && !this.isHidden(parseRef(c.to).nodeId);
  }

  /** Select the wire feeding `inputRef` among those on the board: the only one, or the
   *  primary's when both wires of a mono pair on a USB output are shown. */
  private selectInputWire(inputRef: string): void {
    const wires = this.plan.connections.filter((c) => c.to === inputRef && this.isWireShown(c));
    const pair = monoPairOf(
      this.model,
      inputRef,
      wires.map((c) => c.from),
    );
    const wire = wires.length === 1 ? wires[0] : pair ? wires.find((c) => c.from === ref(pair[0], "out")) : undefined;
    if (wire) this.select({ type: "conn", from: wire.from, to: wire.to });
  }

  private clearPortHighlights(): void {
    const reset = (el: SVGCircleElement, nodeId: string): void => {
      el.setAttribute("fill", this.palette.portOuter);
      el.setAttribute("r", String(JACK_R));
      el.setAttribute("stroke", this.palette.rail[this.nodeById.get(nodeId)!.kind]);
    };
    for (const [r, el] of this.portEls) reset(el, parseRef(r).nodeId);
    for (const [id, el] of this.tapEls) reset(el, id);
  }

  // --- hide / show ---------------------------------------------------------

  /** Shelve every node that has no wires at all. Fixed sends count as wires, so a
   *  channel on just its factory sends is left in place — collapse it by hand
   *  (inspector / multi-select hide) instead. */
  hideUnused(): void {
    if (!this.cb.mayEdit()) return;
    // SD Rec track slots follow their header in a chain, so they are never shelved
    // on their own (shelving the header collapses them via isHidden). The header
    // counts as wired when any of its slots is assigned, so an in-use recorder
    // stays, and an empty one collapses with its slots.
    const wired = (id: string): boolean =>
      this.nodeHasWire(id) || this.attachedDescendants(id).some((d) => this.nodeHasWire(d));
    const unwired = this.model.nodes.filter((n) => !this.isStructuralSlot(n.id) && !wired(n.id));
    const added = unwired.filter((n) => !this.hidden.has(n.id));
    for (const n of unwired) this.hidden.add(n.id);
    this.commitHidden();
    this.dropSelectionIfHidden();
    this.render();
    this.fitView();
    if (added.length) this.cb.onChange();
    this.cb.onStatus(added.length ? t().status.hidUnused(added.length) : t().status.noneToHide);
  }

  /** Shelve one node. Its wires (if any) are hidden along with it. */
  hideNode(id: string): void {
    if (!this.cb.mayEdit()) return;
    this.hidden.add(id);
    this.commitHidden();
    this.dropSelectionIfHidden();
    this.render();
    this.cb.onChange();
    this.cb.onStatus(t().status.hidNode(this.labelOf(id)));
  }

  /** Shelve every node in the multi-selection; their wires are hidden with them. */
  hideSelected(): void {
    const ids = [...this.selectedNodes];
    if (!ids.length) return;
    if (!this.cb.mayEdit()) return;
    const shelvable = ids.filter((id) => !this.hidden.has(id));
    for (const id of shelvable) this.hidden.add(id);
    this.selection = null;
    this.selectedNodes.clear();
    this.commitHidden();
    this.cb.onSelect(null);
    this.render();
    if (shelvable.length) this.cb.onChange();
    this.cb.onStatus(t().status.hidSelected(shelvable.length));
  }

  /** Bring one node back, placed under the viewport so it is easy to find.
   * Parent and hung child (ducker) restore as one unit: a ducker pulls its parent
   * back (it is never shown alone) and a parent brings its child back. Only the
   * parent is placed — the child's position derives from it. */
  showNode(id: string): void {
    if (!this.cb.mayEdit()) return;
    // A chip activated from the keyboard is gone once its node is back, so focus moves on.
    const fromShelf = this.shelf.contains(document.activeElement);
    const at = this.shelfChips().findIndex((c) => c.dataset.node === id);
    const parent = this.parentOf(id);
    let changed = this.hidden.delete(id);
    if (parent) {
      if (this.hidden.delete(parent)) {
        this.placeRestored(parent);
        changed = true;
      }
    } else {
      if (changed) this.placeRestored(id);
      // Restore the whole unit: any hung children (a ducker, the SD Rec slots) that
      // were shelved come back with their parent.
      for (const child of this.attachedDescendants(id)) if (this.hidden.delete(child)) changed = true;
    }
    if (!changed) return;
    this.commitHidden();
    this.render();
    // The chip says a node is coming back and the status line says it came, so it has to be
    // somewhere the operator can see: a node placed beside its STEREO partner goes wherever
    // that partner is, and a hung one wherever its parent is. The node the CHIP named is the
    // one that must land in view — a track slot restored on its own sits below a recorder
    // whose own note can push it off the bottom, and framing the parent shows the parent.
    // After the render, since the pan is measured from what was drawn.
    const partner = this.linkedPartnerOnBoard(parent ?? id);
    this.panIntoView(
      id,
      [parent, partner].filter((x): x is string => x !== undefined && x !== null),
    );
    this.select({ type: "node", id });
    // To the chip that took its place in the row (the last one when it was last), or, with
    // the shelf now empty, to the node it brought back.
    if (fromShelf) {
      const chips = this.shelfChips();
      const next = chips.length ? chips[Math.min(Math.max(at, 0), chips.length - 1)] : this.nodeEls.get(id);
      next?.focus({ preventScroll: true });
    }
    this.cb.onChange();
    this.cb.onStatus(t().status.shownNode(this.labelOf(id)));
  }

  /** The shelf's chips, in the order it shows them. */
  private shelfChips(): HTMLButtonElement[] {
    return [...this.shelf.querySelectorAll<HTMLButtonElement>("button.chip")];
  }

  /** Bring every shelved node back and re-frame the diagram. */
  showAll(): void {
    if (!this.hidden.size) return;
    if (!this.cb.mayEdit()) return;
    const fromShelf = this.shelf.contains(document.activeElement);
    const returning = new Set(this.hidden);
    this.hidden.clear();
    this.commitHidden();
    // A STEREO-linked pair can come back with its two members parked apart — the shelved
    // one kept whatever position it was carrying, which is one nobody has seen since it
    // went to the shelf. Where exactly one member is returning it moves and the node
    // already on the board stays, the same rule a chip restore follows; where both are,
    // neither was on the board, so the pair falls to the primary-keeping sweep below.
    for (const id of returning) {
      const pair = this.linkedPairOf(id);
      if (!pair) continue;
      if (returning.has(pair[0] === id ? pair[1] : pair[0])) continue;
      this.snapToLinkedPartner(id);
    }
    this.alignLinkedPairs();
    this.restackReturning(returning);
    this.render();
    this.fitView();
    // The shelf closes with every chip and Show all on it, so focus goes to the board's tab stop.
    if (fromShelf) {
      const stop = this.rovingId();
      if (stop !== null) this.nodeEls.get(stop)?.focus({ preventScroll: true });
    }
    this.cb.onChange();
    this.cb.onStatus(t().status.shownAll);
  }

  /** Move each returning unit that lands on another node to the foot of its column.
   *
   *  Arrange packs a column over the nodes on the board, so a shelved node comes back to a row
   *  that has since been given to another one, and a member snapped beside its STEREO partner
   *  lands on whatever Arrange put below that partner. A unit is a free-standing node that
   *  returned, or that a returning hung child hangs from, with its linked partner on the board,
   *  whether that partner returned too or is the one it was snapped to; its hung children come
   *  with it. A hung child takes its place from its parent, so one that comes back onto another
   *  node moves its parent with it. A unit moves when a node of it that came back from the shelf
   *  stands on a node outside it; one whose returning nodes stand clear stays where it is, so a
   *  Show all with nothing to restack writes no position. */
  private restackReturning(returning: ReadonlySet<string>): void {
    const placed = new Set<string>();
    for (const node of this.model.nodes) {
      if (!returning.has(node.id)) continue;
      let root = node.id;
      for (let up = this.parentOf(root); up !== undefined; up = this.parentOf(root)) root = up;
      if (placed.has(root) || this.isHidden(root)) continue;
      const partner = this.linkedPartnerOnBoard(root);
      const members = (partner ? [root, partner] : [root]).sort(
        (a, b) => defaultLayoutPos(this.nodeById.get(a)!).y - defaultLayoutPos(this.nodeById.get(b)!).y,
      );
      for (const id of members) placed.add(id);
      const unit = new Set(members.flatMap((id) => [id, ...this.attachedDescendants(id)]));
      const others = this.model.nodes.filter((n) => !unit.has(n.id) && !this.isHidden(n.id)).map((n) => n.id);
      const back = [...unit].filter((id) => returning.has(id) && !this.isHidden(id));
      const lands = back.some((id) => others.some((o) => this.boxesMeet(this.boxOf([id])!, this.boxOf([o])!)));
      if (!lands) continue;
      const col = this.nodeById.get(members[0])!.pos.col;
      const x = MARGIN + col * COL_GAP;
      let y = this.columnFoot(x, unit);
      for (const id of members) {
        this.plan.positions[id] = { x, y };
        y += this.rowsFor(id) * ROW_GAP;
      }
    }
  }

  /** Where the next free row starts below every free-standing node standing across the
   *  column at `x`, outside `skip`: each node's own row plus the rows Arrange advances past it
   *  for its note and hung children. */
  private columnFoot(x: number, skip: ReadonlySet<string>): number {
    let foot = MARGIN;
    for (const node of this.model.nodes) {
      if (node.attachTo || skip.has(node.id) || this.isHidden(node.id)) continue;
      const p = this.posOf(node.id);
      if (p.x >= x + NODE_W - ALIGN_SLOP || x >= p.x + NODE_W - ALIGN_SLOP) continue;
      foot = Math.max(foot, p.y + this.rowsFor(node.id) * ROW_GAP);
    }
    return foot;
  }

  /** Whether two drawn boxes overlap by more than float drift. */
  private boxesMeet(a: { x: number; y: number; w: number; h: number }, b: typeof a): boolean {
    return (
      a.x < b.x + b.w - ALIGN_SLOP &&
      b.x < a.x + a.w - ALIGN_SLOP &&
      a.y < b.y + b.h - ALIGN_SLOP &&
      b.y < a.y + a.h - ALIGN_SLOP
    );
  }

  private commitHidden(): void {
    this.plan.hidden = [...this.hidden];
    this.cb.onHiddenChange(this.plan.hidden);
  }

  /** Whether the anchor selection no longer exists on the canvas: a shelved node, a
   *  wire the plan no longer has, or a wire with a shelved endpoint (which stops being
   *  drawn). One predicate, because the inspector renders from the selection and every
   *  path that can invalidate it — hiding, an undo, a device readback — needs the same
   *  answer. */
  private selectionIsStale(): boolean {
    const sel = this.selection;
    if (!sel) return false;
    if (sel.type === "node") return this.isHidden(sel.id);
    return (
      !hasConnection(this.plan, sel.from, sel.to) ||
      this.isHidden(parseRef(sel.from).nodeId) ||
      this.isHidden(parseRef(sel.to).nodeId)
    );
  }

  // Callers render afterwards, so this only settles the state.
  private dropSelectionIfHidden(): void {
    // Drop any now-hidden id from the multi-selection too, in step with the anchor:
    // a stale hidden id left in selectedNodes keeps redrawWires fading every wire
    // with nothing visibly selected.
    for (const id of [...this.selectedNodes]) if (this.isHidden(id)) this.selectedNodes.delete(id);
    if (this.selectionIsStale()) {
      this.selection = null;
      this.clearTrace();
      this.cb.onSelect(null);
    }
  }

  /** Place a node that has just come back from the shelf. A member of a STEREO-linked
   *  pair whose partner is already on the board lands at its canonical offset from that
   *  partner — the tie is drawn the moment both are up, so the pair arrives together
   *  rather than opening stretched. The one already on the board stays where it is.
   *  Everything else parks under the viewport, where it is easy to find. */
  private placeRestored(id: string): void {
    if (this.linkedPartnerOnBoard(id)) this.snapToLinkedPartner(id);
    else this.placeInView(id);
  }

  /** Pan by the least that brings `ids` inside the viewport, or not at all when they are
   *  already there. The zoom is left alone: a restore is not a re-frame.
   *
   *  A node placed beside its STEREO partner goes wherever that partner is, which can be off
   *  screen — and the shelf answers a chip with a status line saying the node is shown and an
   *  inspector selecting it, so nothing else would say that it is not. Called from the shelf's
   *  single-node restore alone: an alignment the device drives must not move the view under
   *  the operator, and *Show all* re-frames with `fitView` already.
   *
   *  `shown` is the node the chip named — never its parent, though the parent is what carries
   *  a hung node's position: a track slot restored on its own is what the chip, the status line
   *  and the inspector all name. `alongside` (a parent, a STEREO partner) comes with it only
   *  while both fit at the current zoom: a pair too tall for the visible area — a small window,
   *  a deep zoom, an expanded note — would otherwise be framed from its top, which shows the
   *  other one and leaves the node the operator asked for behind the shelf. Where `shown` alone
   *  does not fit either, its top-left corner wins, which is where its header and label are. */
  private panIntoView(shown: string, alongside: readonly string[]): void {
    const own = this.boxOf([shown]);
    if (!own) return;
    const area = this.visibleArea();
    const pad = MARGIN;
    const both = this.boxOf([shown, ...alongside]) ?? own;
    const fits = both.w * this.zoom <= area.w - pad * 2 && both.h * this.zoom <= area.h - pad * 2;
    const box = fits ? both : own;
    let dx = 0;
    let dy = 0;
    const right = (box.x + box.w) * this.zoom + this.pan.x;
    const bottom = (box.y + box.h) * this.zoom + this.pan.y;
    if (right > area.w - pad) dx = area.w - pad - right;
    if (bottom > area.h - pad) dy = area.h - pad - bottom;
    const left = box.x * this.zoom + this.pan.x + dx;
    const top = box.y * this.zoom + this.pan.y + dy;
    if (left < pad) dx += pad - left;
    if (top < pad) dy += pad - top;
    if (dx === 0 && dy === 0) return;
    this.pan = { x: this.pan.x + dx, y: this.pan.y + dy };
    this.applyTransform();
  }

  /** The part of the canvas a node can be SEEN in: the SVG box less the shelf, which covers
   *  the bottom of it and is open exactly when a node is being restored from a chip. */
  private visibleArea(): { w: number; h: number } {
    const rect = this.svg.getBoundingClientRect();
    return {
      w: rect.width || 1000,
      h: (rect.height || 700) - (this.shelf.style.display === "none" ? 0 : this.shelf.offsetHeight),
    };
  }

  /** The content-space box covering `ids`, or null when none of them is on the board. */
  private boxOf(ids: readonly string[]): { x: number; y: number; w: number; h: number } | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const id of ids) {
      if (this.isHidden(id)) continue;
      const p = this.posOf(id);
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + NODE_W);
      maxY = Math.max(maxY, p.y + this.nodeHeight(id));
    }
    return Number.isFinite(minX) ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY } : null;
  }

  /** Park a restored node at the current viewport center, in content coords. */
  private placeInView(id: string): void {
    const rect = this.svg.getBoundingClientRect();
    const cx = ((rect.width || 1000) / 2 - this.pan.x) / this.zoom;
    const cy = ((rect.height || 700) / 2 - this.pan.y) / this.zoom;
    this.plan.positions[id] = { x: cx - NODE_W / 2, y: cy - NODE_H / 2 };
  }

  /** Repaint the bottom shelf from the set of currently-shelved nodes. A ducker
   * hidden alongside its (also hidden) parent gets no chip of its own — the
   * parent's chip restores the whole unit. */
  private renderShelf(): void {
    this.rebuildChrome(this.shelf, () => this.buildShelf());
  }

  private buildShelf(): void {
    const ids = this.model.nodes
      // Chip every USER-shelved node (not merely hidden — a Track-Count-inactive
      // slot is hidden but gated, not shelved, so it gets no chip). A node whose
      // parent is itself shelved is covered by the parent's chip, so skip it.
      .filter((n) => this.hidden.has(n.id) && !(n.attachTo && this.hidden.has(n.attachTo)))
      .map((n) => n.id);
    if (!ids.length) {
      this.shelf.style.display = "none";
      this.shelf.replaceChildren();
      return;
    }
    const m = t();
    const label = document.createElement("div");
    label.className = "shelf-label";
    label.append(document.createTextNode(m.shelf.title));
    const count = document.createElement("span");
    count.className = "shelf-count";
    count.textContent = String(ids.length);
    label.append(count);

    const chips = document.createElement("div");
    chips.className = "shelf-chips";
    for (const id of ids) {
      const node = this.nodeById.get(id)!;
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "chip";
      chip.dataset.node = id;
      chip.style.setProperty("--rail", this.palette.rail[node.kind]);
      // The chip is named by its label's spoken form, as the node it restores is.
      const said = spoken(fullLabel(node));
      if (said !== fullLabel(node)) chip.setAttribute("aria-label", said);
      const name = document.createElement("span");
      name.textContent = fullLabel(node);
      const go = document.createElement("span");
      go.className = "chip-go";
      go.setAttribute("aria-hidden", "true");
      go.textContent = "⤴";
      chip.append(name, go);
      setTip(chip, m.shelf.restore(fullLabel(node)));
      chip.addEventListener("click", () => this.showNode(id));
      chips.append(chip);
    }

    const showAll = document.createElement("button");
    showAll.type = "button";
    showAll.className = "shelf-showall";
    showAll.textContent = m.shelf.showAll;
    showAll.addEventListener("click", () => this.showAll());

    this.shelf.replaceChildren(label, chips, showAll);
    this.shelf.style.display = "flex";
  }

  /** Repaint the floating multi-select action bar. Shown only with two or more
   *  nodes selected (a single selection keeps using the inspector). The hide
   *  button shelves every selected node. */
  private renderSelBar(): void {
    this.rebuildChrome(this.selbar, () => this.buildSelBar());
  }

  private buildSelBar(): void {
    // Only count nodes still on the canvas — a stale shelved id never inflates it.
    const ids = [...this.selectedNodes].filter((id) => this.nodeEls.has(id));
    if (ids.length < 2) {
      this.selbar.style.display = "none";
      this.selbar.replaceChildren();
      return;
    }
    const m = t();

    const label = document.createElement("div");
    label.className = "selbar-label";
    const count = document.createElement("span");
    count.className = "selbar-count";
    count.textContent = String(ids.length);
    label.append(count, document.createTextNode(m.selbar.title));

    const hide = document.createElement("button");
    hide.type = "button";
    hide.className = "selbar-hide";
    hide.textContent = m.selbar.hide(ids.length);
    hide.addEventListener("click", () => this.hideSelected());

    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "selbar-clear";
    clear.textContent = m.selbar.clear;
    clear.addEventListener("click", () => this.clearSelection());

    this.selbar.replaceChildren(label, hide, clear);
    this.selbar.style.display = "flex";
  }

  // --- layout / export -----------------------------------------------------

  autoLayout(): void {
    if (!this.cb.mayEdit()) return;
    // Stack each column top-to-bottom, but snap every node onto the ROW_GAP grid
    // so the result is identical to a fresh plan's default positions (which are
    // pure row * ROW_GAP) — running Arrange on an untouched board moves nothing.
    // A node spanning more than one row (an expanded note, or a hung ducker that
    // the default grid reserves a whole row for) advances by enough whole rows to
    // clear it, so expanded notes still never overlap the node below.
    const colY = new Map<number, number>();
    this.plan.positions = {};
    for (const node of this.model.nodes) {
      if (this.isHidden(node.id)) continue;
      // A hung node (ducker) is positioned by its parent, not laid out on its own.
      if (node.attachTo) continue;
      const col = node.pos.col;
      const y = colY.get(col) ?? MARGIN;
      this.plan.positions[node.id] = { x: MARGIN + col * COL_GAP, y };
      // The vertical footprint of this node plus any hung children it must clear
      // (a ducker, or the stacked SD Rec track slots). Shared with the STEREO pair's own
      // offset, so a pair lands where Arrange would have put its lower member.
      colY.set(col, y + this.rowsFor(node.id) * ROW_GAP);
    }
    this.render();
    this.fitView();
    this.cb.onChange();
    this.cb.onStatus(t().status.arranged);
  }

  private contentBounds(): { x: number; y: number; w: number; h: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const node of this.model.nodes) {
      if (this.isHidden(node.id)) continue;
      const p = this.posOf(node.id);
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + NODE_W);
      // An expanded note panel extends the node's footprint downward; keep it in frame.
      maxY = Math.max(maxY, p.y + this.nodeHeight(node.id));
    }
    if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 100, h: 100 };
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  /** A standalone SVG clone, cropped to content with padding, for image export. */
  private cloneForExport(): { svg: SVGSVGElement; w: number; h: number } {
    const b = this.contentBounds();
    const pad = 28;
    // An export carries nothing of the cue: the board is redrawn without it for the clone and
    // with it again straight after, in one task, so the screen never paints the state between.
    const cueShown = this.cuePathNodes.size > 0;
    if (cueShown) {
      this.cueHeld = true;
      this.redrawWires();
      this.highlightSelectedNode();
    }
    const clone = this.svg.cloneNode(true) as SVGSVGElement;
    if (cueShown) {
      this.cueHeld = false;
      this.redrawWires();
      this.highlightSelectedNode();
    }
    const vp = clone.querySelector("g") as SVGGElement;
    vp.removeAttribute("transform");
    clone.querySelectorAll(".overlay-temp").forEach((el) => el.remove());
    const w = b.w + pad * 2;
    const h = b.h + pad * 2;
    clone.setAttribute("viewBox", `${b.x - pad} ${b.y - pad} ${w} ${h}`);
    clone.setAttribute("width", String(w));
    clone.setAttribute("height", String(h));
    return { svg: clone, w, h };
  }

  // Export scale and theme come from the preferences. A fixed export theme swaps
  // the palette, re-renders, clones, and swaps back — all synchronously in one
  // task, so the on-screen graph never paints the intermediate state.
  private cloneForExportOpts(): { svg: SVGSVGElement; opts: ExportOptions } {
    const { exportScale, exportTheme } = getSettings();
    const target = exportTheme === "active" ? this.themeName : exportTheme;
    const background = PALETTES[target].canvasBg;
    if (target === this.themeName) {
      const { svg, w, h } = this.cloneForExport();
      return { svg, opts: { width: w, height: h, scale: exportScale, background } };
    }
    const prev = this.themeName;
    this.setTheme(target);
    try {
      const { svg, w, h } = this.cloneForExport();
      return { svg, opts: { width: w, height: h, scale: exportScale, background } };
    } finally {
      this.setTheme(prev);
    }
  }

  async exportPng(filename: string): Promise<void> {
    const { svg, opts } = this.cloneForExportOpts();
    const res = await exportSvgToPng(svg, filename, opts, { ext: "png", label: t().filter.png });
    this.cb.onStatus(exportStatus(res, t().status.pngExported));
  }

  async exportPdf(filename: string): Promise<void> {
    const { svg, opts } = this.cloneForExportOpts();
    const res = await exportSvgToPdf(svg, filename, opts, { ext: "pdf", label: t().filter.pdf });
    this.cb.onStatus(exportStatus(res, t().status.pdfExported));
  }
}

function sameSet<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  return a.size === b.size && [...a].every((x) => b.has(x));
}

/** Map a save result to a status line: the saved path, generic done, or cancel. */
function exportStatus(res: SaveResult, doneMsg: string): string {
  if (!res.saved) return t().status.canceled;
  return res.path ? t().status.savedTo(baseName(res.path)) : doneMsg;
}

/** Soft bloom reused by lit jack pins and the selected wire. */
function makeGlowDefs(): SVGDefsElement {
  const defs = document.createElementNS(SVGNS, "defs");
  const filter = document.createElementNS(SVGNS, "filter");
  filter.setAttribute("id", "jack-glow");
  filter.setAttribute("x", "-120%");
  filter.setAttribute("y", "-120%");
  filter.setAttribute("width", "340%");
  filter.setAttribute("height", "340%");
  const blur = document.createElementNS(SVGNS, "feGaussianBlur");
  blur.setAttribute("stdDeviation", "2.2");
  blur.setAttribute("result", "b");
  const merge = document.createElementNS(SVGNS, "feMerge");
  for (const input of ["b", "SourceGraphic"]) {
    const node = document.createElementNS(SVGNS, "feMergeNode");
    node.setAttribute("in", input);
    merge.append(node);
  }
  filter.append(blur, merge);
  defs.append(filter);

  // Soft drop shadow that gives light-theme nodes physical lift.
  const shadow = document.createElementNS(SVGNS, "filter");
  shadow.setAttribute("id", "node-shadow");
  shadow.setAttribute("x", "-20%");
  shadow.setAttribute("y", "-20%");
  shadow.setAttribute("width", "140%");
  shadow.setAttribute("height", "160%");
  const drop = document.createElementNS(SVGNS, "feDropShadow");
  drop.setAttribute("dx", "0");
  drop.setAttribute("dy", "1.5");
  drop.setAttribute("stdDeviation", "1.6");
  drop.setAttribute("flood-color", "rgba(60,45,20,0.35)");
  shadow.append(drop);
  defs.append(shadow);
  return defs;
}

/** One label line at the fixed left inset, vertically centered on `y`. */
function labelText(
  text: string,
  y: number,
  fontSize: number,
  letterSpacing: number,
  fill: string,
  opacity: number,
): SVGTextElement {
  const el = document.createElementNS(SVGNS, "text");
  el.setAttribute("x", String(LABEL_X));
  el.setAttribute("y", String(y));
  el.setAttribute("dominant-baseline", "central");
  el.setAttribute("fill", fill);
  if (opacity !== 1) el.setAttribute("fill-opacity", String(opacity));
  el.setAttribute("font-family", LABEL_FONT);
  el.setAttribute("font-size", String(fontSize));
  el.setAttribute("letter-spacing", String(letterSpacing));
  el.style.pointerEvents = "none";
  el.style.userSelect = "none";
  el.textContent = text;
  return el;
}

// The decorative half of a jack: the socket ring and the pin that lights inside it
// when the jack carries a live wire. Both are pointer-transparent — an oversized
// hit target drawn beneath them receives the pointer instead. Shared by the side
// ports and the Rec Point tap so the two read as the same connector.
function jackPair(cx: number, cy: number, rail: string, p: Palette): { jack: SVGCircleElement; pin: SVGCircleElement } {
  const jack = document.createElementNS(SVGNS, "circle");
  jack.setAttribute("cx", String(cx));
  jack.setAttribute("cy", String(cy));
  jack.setAttribute("r", String(JACK_R));
  jack.setAttribute("fill", p.portOuter);
  jack.setAttribute("stroke", rail);
  jack.setAttribute("stroke-width", "1.5");
  jack.style.pointerEvents = "none";

  const pin = document.createElementNS(SVGNS, "circle");
  pin.classList.add("port-pin");
  pin.setAttribute("cx", String(cx));
  pin.setAttribute("cy", String(cy));
  pin.setAttribute("r", String(PIN_R_OFF));
  pin.setAttribute("fill", p.portPinOff);
  pin.style.pointerEvents = "none";
  return { jack, pin };
}

function svgRect(x: number, y: number, w: number, h: number, rx: number, fill: string): SVGRectElement {
  const r = document.createElementNS(SVGNS, "rect");
  r.setAttribute("x", String(x));
  r.setAttribute("y", String(y));
  r.setAttribute("width", String(w));
  r.setAttribute("height", String(h));
  r.setAttribute("rx", String(rx));
  r.setAttribute("fill", fill);
  r.style.pointerEvents = "none";
  return r;
}

function svgLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  stroke: string,
  width: number,
  opacity: number,
): SVGLineElement {
  const l = document.createElementNS(SVGNS, "line");
  l.setAttribute("x1", String(x1));
  l.setAttribute("y1", String(y1));
  l.setAttribute("x2", String(x2));
  l.setAttribute("y2", String(y2));
  l.setAttribute("stroke", stroke);
  l.setAttribute("stroke-width", String(width));
  l.setAttribute("stroke-linecap", "round");
  l.setAttribute("opacity", String(opacity));
  l.style.pointerEvents = "none";
  return l;
}
